import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaFacebook,
  FaInstagram,
  FaXTwitter,
  FaLinkedin,
  FaTiktok,
  FaYoutube,
  FaPinterest,
  FaThreads,
  FaBluesky,
} from 'react-icons/fa6';
import {
  CalendarDays,
  Sparkles,
  Users,
  BarChart3,
  Clock,
  Zap,
  Image as ImageIcon,
  MessageSquare,
  Bot,
  Check,
  CheckCircle2,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  Hash,
  Smile,
  MapPin,
  MoreHorizontal,
  Maximize2,
  TrendingUp,
  MousePointer2,
  Upload,
  Film,
  Heart,
  Eye,
  Palette,
  Layers,
  Globe,
  Send,
  AtSign,
  Code,
  Terminal,
  Star,
  UserCheck,
  Pencil,
  Mail,
} from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { SiteFooter } from '../components/SiteFooter';
import { PricingPlans } from '../components/PricingPlans';
import { RoadmapSection } from '../components/RoadmapSection';
import { BlogTeaser } from '../components/BlogTeaser';
import { useSeo } from '../lib/seo';

type Net = { Icon: typeof FaFacebook; name: string; color: string };

const NETWORKS: Net[] = [
  { Icon: FaInstagram, name: 'Instagram', color: '#E4405F' },
  { Icon: FaXTwitter,  name: 'X',         color: '#0F1419' },
  { Icon: FaLinkedin,  name: 'LinkedIn',  color: '#0A66C2' },
  { Icon: FaFacebook,  name: 'Facebook',  color: '#1877F2' },
  { Icon: FaTiktok,    name: 'TikTok',    color: '#000000' },
  { Icon: FaYoutube,   name: 'YouTube',   color: '#FF0000' },
  { Icon: FaThreads,   name: 'Threads',   color: '#000000' },
  { Icon: FaPinterest, name: 'Pinterest', color: '#BD081C' },
  { Icon: FaBluesky,   name: 'Bluesky',   color: '#0085FF' },
];


type Testimonial = {
  quote: string;
  name: string;
  role: string;
  initials: string;
  gradient: string; // tailwind gradient classes for the avatar
};

const TESTIMONIALS: Testimonial[] = [
  {
    quote: 'Antes pasaba la tarde del domingo programando posts. Ahora dejo toda la semana lista en una hora.',
    name: 'Marta López',
    role: 'Community Manager · Estudio Bonita',
    initials: 'ML',
    gradient: 'from-rose-400 to-pink-500',
  },
  {
    quote: 'La predicción de IA me ahorra subir cualquier cosa: te dice si el copy funciona antes de publicar.',
    name: 'Carlos Ruiz',
    role: 'Freelance Social Media',
    initials: 'CR',
    gradient: 'from-brand-500 to-indigo-500',
  },
  {
    quote: 'Llevo 4 cuentas de clientes a la vez. Zettapost me ha dado de vuelta los fines de semana.',
    name: 'Sofía Méndez',
    role: 'Marketing Manager · Verde Bistró',
    initials: 'SM',
    gradient: 'from-emerald-400 to-teal-500',
  },
  {
    quote: 'La vista de calendario con drag & drop es lo que más uso. Por fin programar es algo visual.',
    name: 'Adrián Torres',
    role: 'Fundador · Lumen Studio',
    initials: 'AT',
    gradient: 'from-amber-400 to-orange-500',
  },
  {
    quote: 'Probé Buffer y Hootsuite. Zettapost es lo único que entiende los formatos de cada red de verdad.',
    name: 'Lucía Vidal',
    role: 'Creator & Coach',
    initials: 'LV',
    gradient: 'from-violet-500 to-purple-600',
  },
  {
    quote: 'Subo el material un día y la cola semanal hace todo el reparto. Magia para mi cabeza desordenada.',
    name: 'Pablo Serrano',
    role: 'Social Media Lead · Aura Cosmetics',
    initials: 'PS',
    gradient: 'from-fuchsia-500 to-pink-500',
  },
  {
    quote: 'La preview en cada red me ha evitado mil meteduras de pata con dimensiones y caracteres cortados.',
    name: 'Elena Castro',
    role: 'Agencia Social Plus',
    initials: 'EC',
    gradient: 'from-sky-400 to-blue-500',
  },
  {
    quote: 'Por fin un panel de analítica que junta todas las redes. No más capturas de cada app.',
    name: 'Diego Martín',
    role: 'Marketing Digital · Café Norte',
    initials: 'DM',
    gradient: 'from-cyan-400 to-sky-500',
  },
  {
    quote: 'Publico el mismo Reel en TikTok, Instagram y YouTube Shorts en menos de un minuto. Game changer.',
    name: 'Andrea Pérez',
    role: 'Content Creator',
    initials: 'AP',
    gradient: 'from-pink-500 to-rose-500',
  },
  {
    quote: 'Tener los colores y el tono de marca guardados hace que la IA suene a nosotros, no a un robot.',
    name: 'Javier Romero',
    role: 'CMO · Plana Studio',
    initials: 'JR',
    gradient: 'from-indigo-500 to-violet-500',
  },
  {
    quote: 'Hemos pasado de 3 apps y 2 hojas de Excel a Zettapost. Punto. El equipo está mucho más relajado.',
    name: 'Nuria Blanco',
    role: 'Social Media Manager · Ola Surf',
    initials: 'NB',
    gradient: 'from-teal-400 to-emerald-500',
  },
  {
    quote: 'Lo que más valoro: cuando algo falla, te avisa. No descubres a las 2 semanas que un post no salió.',
    name: 'Tomás García',
    role: 'Brand Manager · Hola Snacks',
    initials: 'TG',
    gradient: 'from-orange-400 to-red-500',
  },
];


type Faq = { q: string; a: string };

const FAQS: Faq[] = [
  {
    q: '¿Qué redes sociales puedo conectar?',
    a: 'Instagram, X (Twitter), LinkedIn, Facebook, TikTok, YouTube, Threads, Pinterest y Bluesky. Usamos las APIs oficiales de cada plataforma para que tus cuentas estén siempre seguras y conectadas.',
  },
  {
    q: '¿Puedo cambiar o cancelar mi plan en cualquier momento?',
    a: 'Sí. Puedes subir, bajar o cancelar tu suscripción cuando quieras desde la sección de Facturación. No hay permanencia ni penalizaciones por cancelar.',
  },
  {
    q: '¿Cuántas publicaciones puedo programar al mes?',
    a: 'No hay límite de publicaciones en ningún plan. Los planes se diferencian por el número de cuentas conectadas y por funciones avanzadas como la IA o los informes.',
  },
  {
    q: '¿Cómo funciona la predicción de rendimiento con IA?',
    a: 'Antes de publicar, nuestra IA analiza tu copy y tus imágenes y te da una puntuación de 0 a 100 con sugerencias concretas para mejorar (gancho, longitud, hashtags, claridad…). Así publicas con más confianza.',
  },
  {
    q: '¿Puedo gestionar las redes de varios clientes?',
    a: 'Sí, está pensado exactamente para eso. Puedes agrupar cuentas por marca, guardar la identidad visual de cada cliente (colores, logo, tono de voz) y trabajar con varias marcas en paralelo desde el mismo panel.',
  },
  {
    q: '¿Puedo programar Reels, Stories y vídeos verticales?',
    a: 'Sí. Soportamos los formatos nativos de cada red: posts, Reels, Stories, Shorts y Pins.',
  },
  {
    q: '¿Qué pasa si una red social rechaza una publicación?',
    a: 'Te avisamos al momento con el error concreto que devuelve la plataforma (carácter inválido, imagen demasiado grande, sesión caducada, etc.) y te dejamos reintentar con un solo clic. Nada se queda en silencio.',
  },
  {
    q: '¿Mis datos y los de mis clientes están seguros?',
    a: 'Sí. Conectamos vía OAuth oficial (nunca pedimos contraseñas), ciframos todo en tránsito y servimos desde infraestructura europea. Tú eres siempre el dueño de tus datos y puedes exportarlos o eliminarlos cuando quieras.',
  },
  {
    q: '¿Hay app móvil?',
    a: 'Zettapost funciona perfectamente en el navegador del móvil. Una app nativa para iOS y Android está en el roadmap; por ahora puedes guardar la web como acceso directo en tu pantalla de inicio.',
  },
];


export function Landing() {
  useSeo({
    title: 'Zettapost · Programador de redes sociales con IA · Prueba gratis',
    description:
      'Programa y publica en todas tus redes sociales desde un solo lugar. IA para captions, ideas y predicción de rendimiento. Prueba 3 días gratis.',
    canonical: 'https://zettapost.com/',
  });

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white overflow-x-hidden">

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-white">
        {/* subtle dot grid pattern */}
        <div
          aria-hidden
          className="absolute inset-0 -z-10 opacity-40 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgb(203 213 225) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          }}
        />
        {/* Aurora orbs */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -left-20 h-[420px] w-[420px] rounded-full bg-gradient-to-br from-brand-300/50 via-sky-300/30 to-transparent blur-3xl -z-10 animate-hero-orb-a"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute top-20 -right-24 h-[480px] w-[480px] rounded-full bg-gradient-to-br from-indigo-300/40 via-violet-200/30 to-transparent blur-3xl -z-10 animate-hero-orb-b"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-1/3 h-[320px] w-[320px] rounded-full bg-gradient-to-tr from-pink-200/30 via-amber-100/20 to-transparent blur-3xl -z-10"
        />

        <div className="relative max-w-7xl mx-auto px-6 pt-10 pb-20 lg:pt-16 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          {/* ── LEFT: copy ──────────────────────────────────── */}
          <div className="lg:col-span-5 animate-fade-in-up">
            {/* Trust pill */}
            <div className="inline-flex items-center gap-2.5 rounded-full bg-white/80 backdrop-blur border border-slate-200 px-3 py-1.5 shadow-sm">
              <img
                src="/images-users.webp"
                alt="Usuarios de Zettapost"
                className="h-6 w-auto"
              />
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={11} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <span className="text-xs font-semibold text-slate-700">
                +2.300 creadores confían en Zettapost
              </span>
            </div>

            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[50px] font-extrabold tracking-tight text-slate-900 leading-[0.98]">
              <RotatingHeadline />
            </h1>

            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Planifica tu contenido, adapta cada post a su plataforma y deja tus redes programadas para toda la semana desde un único lugar.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/register"
                className="btn-accent text-base px-7 py-3.5 gap-2 group rounded-xl shadow-lg shadow-amber-500/20 hover:shadow-xl hover:shadow-amber-500/30 transition-shadow"
              >
                Regístrate gratis
                <ArrowRight size={18} className="transition group-hover:translate-x-1" />
              </Link>
              <Link
                to="/login"
                className="btn-secondary text-base px-7 py-3.5 rounded-xl bg-white hover:bg-slate-50 border border-slate-200"
              >
                Inicia sesión
              </Link>
            </div>

            <div className="mt-5 flex items-center gap-2 text-sm text-slate-500">
              <CheckCircle2 size={16} className="text-emerald-500" />
              Pruébalo gratis durante 3 días. Cancela cuando quieras.
            </div>

            {/* Compact network row */}
            <div className="mt-10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">
                Publica en
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {NETWORKS.map((n) => (
                  <span
                    key={n.name}
                    title={n.name}
                    className="group inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                  >
                    <n.Icon size={16} color={n.color} />
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* ── RIGHT: app screenshot ───────────────────────── */}
          <div className="lg:col-span-7 relative animate-fade-in-up">
            <div className="relative w-full">
              {/* Layered glow behind the screenshot */}
              <div
                aria-hidden
                className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-brand-200 via-sky-100 to-indigo-200 blur-2xl opacity-60 -z-10"
              />
              <div
                aria-hidden
                className="absolute -inset-10 rounded-[2.5rem] bg-gradient-to-tr from-amber-100/40 via-transparent to-pink-100/40 blur-3xl -z-20"
              />

              {/* Screenshot with float animation */}
              <div className="relative animate-hero-float">
                <img
                  src="/hero1-optimized.webp"
                  alt="Panel de creación de publicaciones de Zettapost"
                  className="relative w-full rounded-2xl shadow-2xl border border-slate-200/60 ring-1 ring-black/5"
                  loading="eager"
                />

                {/* Floating card 1 — Published */}
                <div className="hidden sm:block absolute -top-4 -left-4 lg:-left-10 animate-hero-pop-1 z-10">
                  <div className="flex items-center gap-2.5 rounded-xl bg-white shadow-xl border border-slate-200 px-3 py-2.5 ring-1 ring-black/5">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                      <CheckCircle2 size={18} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[12px] font-bold text-slate-900 leading-tight">
                        Publicado en Instagram
                      </p>
                      <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-hero-live" />
                        hace 2 segundos
                      </p>
                    </div>
                  </div>
                </div>

                {/* Floating card 2 — Engagement */}
                <div className="hidden sm:block absolute top-1/2 -right-3 lg:-right-8 -translate-y-1/2 animate-hero-pop-2 z-10">
                  <div className="rounded-xl bg-white shadow-xl border border-slate-200 px-3.5 py-3 ring-1 ring-black/5 w-44">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        <TrendingUp size={11} className="text-emerald-500" />
                        Engagement
                      </span>
                      <span className="text-[10px] font-bold text-emerald-600">+47%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-brand-500 animate-hero-bar-fill" />
                    </div>
                    <p className="mt-2 text-[11px] text-slate-700 font-semibold leading-tight">
                      Tu mejor semana<br />del mes 🎉
                    </p>
                  </div>
                </div>

                {/* Floating card 3 — Scheduled posts */}
                <div className="hidden sm:block absolute -bottom-5 left-6 lg:left-12 animate-hero-pop-3 z-10">
                  <div className="flex items-center gap-2.5 rounded-xl bg-white shadow-xl border border-slate-200 px-3 py-2.5 ring-1 ring-black/5">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                      <CalendarDays size={18} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[12px] font-bold text-slate-900 leading-tight">
                        12 posts programados
                      </p>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        para esta semana
                      </p>
                    </div>
                    {/* Stacked avatars hint */}
                    <div className="flex -space-x-1 ml-1">
                      {[FaInstagram, FaXTwitter, FaLinkedin].map((Icon, i) => (
                        <span
                          key={i}
                          className="h-5 w-5 rounded-full bg-white border border-slate-200 flex items-center justify-center"
                        >
                          <Icon size={9} className="text-slate-700" />
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MARQUEE NETWORKS ─────────────────────────────────── */}
      <section className="border-y border-slate-200 bg-white marquee-section">
        <div className="py-6">
          <p className="text-center text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-4">
            Una herramienta PARA todas las redes que te importan.
          </p>
          <div className="relative overflow-hidden" style={{ transform: 'translateZ(0)' }}>
            <div className="flex gap-12 animate-marquee whitespace-nowrap">
              {[...NETWORKS, ...NETWORKS].map((n, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-700 shrink-0">
                  <n.Icon size={22} color={n.color} />
                  <span className="text-sm font-medium">{n.name}</span>
                </div>
              ))}
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-white to-transparent" style={{ transform: 'translateZ(0)' }} />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-white to-transparent" style={{ transform: 'translateZ(0)' }} />
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────── */}
      <section id="como-funciona" className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-600">Cómo funciona</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900">
            Tres pasos para publicar.<br />
            <span>en todas tus redes.</span>
          </h2>
          <p className="mt-3 text-slate-600">Sube tu contenido, deja que la IA te ayude con el copy y programa tus publicaciones cuando quieras.</p>
        </div>

        <div className="mt-16 grid lg:grid-cols-3 gap-10 lg:gap-8">
          <HowItWorksStep
            number="01"
            title="Sube tus fotos y vídeos"
            body="Arrastra varios archivos a la vez. Los guardamos en tu biblioteca para usarlos en cualquier publicación."
            Mockup={UploadMockup}
          />
          <HowItWorksStep
            number="02"
            title="Redacta con la ayuda de la IA"
            body="Escribe tú o deja que la IA proponga el copy. Te enseñamos al instante cómo va a rendir antes de publicarlo."
            Mockup={ComposerMockup}
          />
          <HowItWorksStep
            number="03"
            title="Elige tus redes y programa"
            body="Marca las cuentas destino, fija el horario y olvídate. Zettapost publica por ti y te avisa si algo falla."
            Mockup={NetworksMockup}
          />
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────── */}
      <section id="caracteristicas" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-600">Todo lo que puedes hacer con Zettapost</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900">Ahorra horas cada semana gestionando tus redes.</h2>
            <p className="mt-3 text-slate-600">Todo lo que necesitas para crear, programar y medir tu contenido en todas tus redes, desde un mismo lugar.</p>
          </div>

          {/* Bento grid */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[minmax(0,1fr)]">
            <FeatureCard
              eyebrow="Multi-red"
              title="Publica en todas tus redes sociales a la vez."
              body="Redacta una publicación y publica automáticamente en Instagram, X, LinkedIn, TikTok, Facebook, YouTube, Threads, Pinterest y Bluesky. Sin tener que saltar entre apps ni hacerlo manualmente."
              wide
              Mockup={MultiNetworkMockup}
            />
            <FeatureCard
              eyebrow="IA"
              title="Predicción de rendimiento"
              body="Antes de publicar, nuestra IA puntúa tu post y te sugiere mejoras concretas. Para que publiques con toda la confianza de un equipo de marketing autónomo."
              badge="NUEVO"
              Mockup={AiFeatureMockup}
            />

            <FeatureCard
              eyebrow="Calendario"
              title="Arrastra. Suelta. Reprograma."
              body="Visualiza un mes entero de contenido o revisa tu semana fácilmente. Cambia un post de día solo arrastrándolo fácilmente."
              Mockup={CalendarFeatureMockup}
            />
            <FeatureCard
              eyebrow="Cola semanal"
              title="Tus huecos perfectos"
              body="Define unos horarios y envía tus próximas publicaciones a la cola para que se publiquen automáticamente en el siguiente hueco disponible."
              Mockup={QueueFeatureMockup}
            />
            <FeatureCard
              eyebrow="Formatos"
              title="Post, Reel, Story o Pin"
              body="Gestionamos cómo funciona cada plataforma para adaptar tu contenido a ella."
              Mockup={FormatsFeatureMockup}
            />

            <FeatureCard
              eyebrow="Vista previa"
              title="Mira cómo queda en cada red social"
              body="Maquetamos tu post tal y como se verá en cada plataforma para que puedas ver cómo quedará antes de publicarlo."
              Mockup={PreviewsFeatureMockup}
            />
            <FeatureCard
              eyebrow="Marca"
              title="Tu marca, organizada"
              body="Agrupa cuentas por marca, guarda tus colores de marca, logo y tono de voz para que todas las funciones de IA creen el contenido que tu marca se merece."
              Mockup={BrandFeatureMockup}
            />
            <FeatureCard
              eyebrow="Texto por red"
              title="Un post, diferentes textos en cada red"
              body="Escribe el texto general una vez y, si quieres, personalízalo por red social: emojis y hashtags para Instagram, breve en X, profesional en LinkedIn. Si dejas vacío, vuelve al general automáticamente."
              badge="NUEVO"
              Mockup={PerNetworkCaptionFeatureMockup}
            />

            <FeatureCard
              eyebrow="Analítica"
              title="Métricas que importan"
              body="Analiza el crecimiento de tus cuentas en todas las redes sociales conectadas desde un único lugar. También puedes descargarte reportes para compartir con tu equipo."
              Mockup={AnalyticsFeatureMockup}
            />
            <FeatureCard
              eyebrow="Link in bio"
              title="Link in bio"
              body="Una página para todos tus enlaces totalmente personalizada y con captura de email gratis. Personaliza colores, fondo y banner; añade botones a tus redes; y mide los clics."
              badge="NUEVO"
              Mockup={LinkInBioFeatureMockup}
            />
            <FeatureCard
              eyebrow="Zona horaria"
              title="Publicado a la hora correcta"
              body="Todos los posts se programan en tu zona horaria, que puedes editar para que se publiquen a la hora correcta, sin problemas de cambios horarios."
              Mockup={TimezoneFeatureMockup}
            />

            <FeatureCard
              eyebrow="Inspiración"
              title="Una plantilla nueva cada día"
              body="Cada día generamos con IA una plantilla por nicho (restaurantes, fitness, ecommerce, coaches y más) con imagen y caption listos. Cópialos a tu composer en un clic."
              badge="NUEVO"
              Mockup={InspirationFeatureMockup}
            />
            <FeatureCard
              eyebrow="Usa nuestra IA"
              title="Genera copys en segundos"
              body="¿Atascad@ sin saber qué poner en el copy? Con nuestra IA lo tendrás en segundos: ve exactamente tu post, incluida la foto o el vídeo, y escribe el caption por ti."
              badge="NUEVO"
              Mockup={AiCaptionFeatureMockup}
            />
            <FeatureCard
              eyebrow="API"
              title="API Disponible"
              body="Publica y programa desde tu código o IA con nuestra API. Un token, un endpoint. Incluida en todos los planes."
              badge="NUEVO"
              Mockup={ApiFeatureMockup}
            />
            <FeatureCard
              eyebrow="ChatGPT · MCP"
              title="Gestiona tus redes desde ChatGPT"
              body="Conecta Zettapost a ChatGPT, Claude o Cursor con nuestro conector MCP y crea, programa, edita y mide tus redes hablando con la IA."
              badge="NUEVO"
              Mockup={McpFeatureMockup}
            />
            <FeatureCard
              eyebrow="Aprobación de cliente"
              title="Aprobación antes de publicar"
              body="Envía el post a tu cliente por enlace o por email para que pueda aprobar su publicación o pedirte que realices cambios."
              badge="NUEVO"
              Mockup={ApprovalFeatureMockup}
            />
            <FeatureCard
              eyebrow="Equipos"
              title="Trabaja en equipo"
              body="Invita a tus compañeros por email o enlace, asígnales las cuentas y secciones a las que pueden acceder y gestionad las redes juntos desde un mismo espacio."
              badge="NUEVO"
              Mockup={TeamFeatureMockup}
            />
          </div>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────── */}
      <section className="relative bg-white py-20 lg:py-28 overflow-hidden">
        {/* subtle dot grid pattern */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            backgroundImage: 'radial-gradient(circle, rgb(203 213 225) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          }}
        />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-600">Precios</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900">
              Elige el plan<br />
              <span>que mejor encaje contigo.</span>
            </h2>
            <p className="mt-3 text-slate-600">
              Empieza con 3 días gratis. Cambia o cancela tu plan cuando quieras.
            </p>
          </div>

          <PricingPlans />
        </div>
      </section>

      {/* ── TESTIMONIOS ──────────────────────────────────────── */}
      <section className="bg-slate-50 border-y border-slate-200 py-20 lg:py-28 overflow-hidden marquee-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-600">Testimonios</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900">
              Lo que dicen<br />
              <span>quienes ya lo usan.</span>
            </h2>
            <p className="mt-3 text-slate-600">
              Community managers, freelancers y agencias cuentan cómo Zettapost les ha cambiado el flujo de trabajo.
            </p>
          </div>
        </div>

        {/* Slider con dos filas de marquee — direcciones opuestas */}
        <div className="relative mt-14" style={{ transform: 'translateZ(0)' }}>
          {/* Degradados laterales */}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 sm:w-32 bg-gradient-to-r from-slate-50 to-transparent" style={{ transform: 'translateZ(0)' }} />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 sm:w-32 bg-gradient-to-l from-slate-50 to-transparent" style={{ transform: 'translateZ(0)' }} />

          <div
            className="flex gap-5 animate-marquee w-max"
            style={{ animationDuration: '60s' }}
          >
            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
              <TestimonialCard key={`${t.name}-${i}`} {...t} />
            ))}
          </div>
        </div>
      </section>

      {/* ── MID CTA ──────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white border-y border-slate-100">
        {/* subtle orb blobs */}
        <div aria-hidden className="pointer-events-none absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-brand-100/60 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-40 -right-40 h-[500px] w-[500px] rounded-full bg-indigo-100/50 blur-3xl" />

        <div className="relative max-w-3xl mx-auto px-6 py-20 lg:py-28 text-center">
          {/* eyebrow */}
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 border border-brand-100 px-4 py-1.5 text-xs font-semibold text-brand-600">
            <Clock size={13} />
            Ahorra una media de <strong>6 horas semanales</strong>
          </span>

          <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
            ¿Cuántas horas pierdes
            <br className="hidden sm:block" />
            publicando{' '}
            <span className="text-brand-600">a mano?</span>
          </h2>

          <p className="mt-5 text-slate-500 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
            Olvídate de saltar entre apps. Crea, programa y publica en todas tus redes desde un único lugar.
          </p>

          <div className="mt-8">
            <Link
              to="/register"
              className="btn-primary text-base px-8 py-3.5 gap-2 group rounded-xl shadow-md shadow-brand-500/20 hover:shadow-brand-500/30 transition-shadow"
            >
              Pruébanos gratis 3 días
              <ArrowRight size={18} className="transition group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="mt-5 flex flex-wrap justify-center items-center gap-x-5 gap-y-2 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={13} className="text-emerald-500" />
              Prueba gratis 3 días
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={13} className="text-emerald-500" />
              Cancela cuando quieras
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={13} className="text-emerald-500" />
              Sin permanencia
            </span>
          </div>
        </div>
      </section>

      {/* ── ROADMAP (dynamic + votable) ──────────────────────── */}
      <RoadmapSection />

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section id="faqs" className="bg-slate-50 border-y border-slate-200 py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-600">Preguntas frecuentes</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900">
              Todo lo que quieres saber<br />
              <span>antes de empezar.</span>
            </h2>
            <p className="mt-3 text-slate-600">
              ¿Te queda alguna duda? Escríbenos a <a href="mailto:hello@zettapost.com" className="text-brand-600 font-medium hover:underline">hello@zettapost.com</a><br />
              y te respondemos en menos de 24 h.
            </p>
          </div>

          <div className="mt-12 space-y-3">
            {FAQS.map((item, i) => (
              <details
                key={i}
                className="group rounded-2xl bg-white border border-slate-200 hover:border-slate-300 transition-colors open:shadow-md open:border-brand-200"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-5 py-4 sm:px-6 sm:py-5">
                  <span className="font-semibold text-slate-900 text-base sm:text-lg leading-snug">
                    {item.q}
                  </span>
                  <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 group-open:bg-brand-100 group-open:text-brand-700 text-slate-500 transition-all">
                    <ChevronDown size={18} className="transition-transform duration-300 group-open:rotate-180" />
                  </span>
                </summary>
                <div className="px-5 sm:px-6 pb-5 -mt-1">
                  <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                    {item.a}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── BLOG TEASER (últimos posts) ──────────────────────── */}
      <BlogTeaser />

      {/* ── FINAL CTA ────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white to-slate-50" />
        <div className="max-w-3xl mx-auto px-6 py-20 lg:py-28 text-center">
          {/* logo */}
          <div className="flex justify-center mb-6">
            <img src="/zettapost-logo-min.png" alt="Zettapost" className="h-10 w-auto" />
          </div>
          <div className="flex justify-center gap-2 mb-8">
            {NETWORKS.slice(0, 6).map((n) => (
              <div
                key={n.name}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white border border-slate-200 shadow-sm"
              >
                <n.Icon size={18} color={n.color} />
              </div>
            ))}
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            La forma más fácil de estar
            <br />
            <span className="text-brand-600">en todas partes.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600">
            Crea tu cuenta y pruébanos gratis durante 3 días. Cancela cuando quieras.
          </p>
          <div className="mt-8 flex justify-center">
            <Link to="/register" className="btn-accent text-base px-7 py-3 gap-2 group rounded-xl">
              Crea tu cuenta ahora
              <ArrowRight size={18} className="transition group-hover:translate-x-1" />
            </Link>
          </div>
          <p className="mt-5 text-sm text-slate-500">Estás a un paso de comenzar a publicar en todas tus redes fácilmente.</p>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────── */}
      <SiteFooter />
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   HERO CAROUSEL — one mockup at a time + caption below
   Auto-advances every 5s. Click dots to jump.
   Order: Calendar first.
   ───────────────────────────────────────────────────────────── */
const SLIDES: {
  Mockup: () => JSX.Element;
  title: string;
  desc: string;
}[] = [
  {
    Mockup: CalendarMockup,
    title: 'Calendario visual',
    desc: 'Ve todo tu mes de un vistazo. Arrastra, suelta y reorganiza tu contenido sin abrir 8 pestañas.',
  },
  {
    Mockup: CreatePostMockup,
    title: 'Editor multicanal',
    desc: 'Escribe una vez y adapta el copy por cada red. Hashtags, media y programación, todo en el mismo sitio.',
  },
  {
    Mockup: MultichannelMockup,
    title: 'Publica en todas a la vez',
    desc: 'Un contenido, todas tus redes. Cero copia-pega y cero olvidos: Zettapost publica por ti en el momento exacto.',
  },
  {
    Mockup: PerformanceMockup,
    title: 'Analítica unificada',
    desc: 'Alcance, interacciones y clics de todas tus redes en un solo panel. Compara, exporta, decide.',
  },
  {
    Mockup: ApprovalsMockup,
    title: 'Flujo de aprobaciones',
    desc: 'Tu equipo revisa, aprueba y comenta sin salir de Zettapost. Adiós a los Google Docs eternos.',
  },
  {
    Mockup: QueueMockup,
    title: 'Cola de publicaciones',
    desc: 'Vista cronológica de todo lo que sale hoy, mañana y la semana que viene. Sin sorpresas.',
  },
];

const HEADLINES = [
  // First entry is what Google sees in the H1 — keep it keyword-rich.
  { pre: 'Programa y publica en', accent: 'todas tus redes sociales.' },
  { pre: 'Tu calendario de redes,', accent: 'por fin bajo control.' },
  { pre: 'De una idea a todas tus redes sociales', accent: 'en minutos.' },
];

function RotatingHeadline() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % HEADLINES.length);
        setVisible(true);
      }, 400);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  const h = HEADLINES[idx]!;
  return (
    <span
      style={{
        transition: 'opacity 0.4s ease, transform 0.4s ease',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(10px)',
        display: 'inline-block',
      }}
    >
      {h.pre}{' '}
      <span className="text-brand-600">{h.accent}</span>
    </span>
  );
}

function HeroCarousel() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((p) => (p + 1) % SLIDES.length), 5000);
    return () => clearInterval(t);
  }, [paused]);

  const current = SLIDES[i]!;
  const Mockup = current.Mockup;

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* mockup stage */}
      <div className="relative h-[540px] lg:h-[600px] rounded-2xl">
        {/* the mockup, generously sized */}
        <div
          key={i}
          className="absolute inset-0 flex items-center justify-center px-4 animate-fade-in-up"
        >
          <div className="w-full max-w-[640px] drop-shadow-2xl">
            <Mockup />
          </div>
        </div>

      </div>

      {/* feature pills — active one is highlighted with a check badge */}
      <div className="mt-4 flex flex-wrap justify-center gap-2.5 max-w-xl mx-auto">
        {SLIDES.map((s, idx) => {
          const active = idx === i;
          return (
            <button
              key={idx}
              onClick={() => setI(idx)}
              aria-label={`Ver ${s.title}`}
              aria-current={active}
              className={`relative inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium transition ${
                active
                  ? 'bg-brand-50 text-brand-700 border-brand-200 pr-9'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {s.title}
              {active && (
                <span className="absolute -right-1.5 -top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white shadow-sm ring-2 ring-white">
                  <Check size={12} strokeWidth={3} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── CALENDAR card ──────────────────────────────────────── */
function CalendarMockup() {
  const days = [
    { lbl: 'LUN', d: 20 },
    { lbl: 'MAR', d: 21 },
    { lbl: 'MIÉ', d: 22 },
    { lbl: 'JUE', d: 23 },
    { lbl: 'VIE', d: 24, today: true },
    { lbl: 'SÁB', d: 25 },
    { lbl: 'DOM', d: 26 },
  ];
  const hours = ['08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'];

  const events = [
    { top: 8,   col: 1, bg: 'bg-pink-50',   br: 'border-pink-200',   tc: 'text-pink-700',    Icon: FaInstagram, c: '#E4405F', time: '10:00', title: 'Reel diario' },
    { top: 8,   col: 2, bg: 'bg-blue-50',   br: 'border-blue-200',   tc: 'text-blue-700',    Icon: FaLinkedin,  c: '#0A66C2', time: '09:30', title: 'Estrategia' },
    { top: 8,   col: 4, bg: 'bg-pink-50',   br: 'border-brand-500',  tc: 'text-pink-700',    Icon: FaInstagram, c: '#E4405F', time: '10:00', title: 'Nuevo post' },
    { top: 70,  col: 1, bg: 'bg-slate-50',  br: 'border-slate-200',  tc: 'text-slate-700',   Icon: FaXTwitter,  c: '#0F1419', time: '12:00', title: 'Hilo SaaS' },
    { top: 70,  col: 6, bg: 'bg-red-50',    br: 'border-red-200',    tc: 'text-red-700',     Icon: FaYoutube,   c: '#FF0000', time: '11:00', title: 'Vídeo' },
    { top: 132, col: 3, bg: 'bg-blue-50',   br: 'border-blue-200',   tc: 'text-blue-700',    Icon: FaFacebook,  c: '#1877F2', time: '14:00', title: 'Live', drag: true },
    { top: 194, col: 4, bg: 'bg-slate-900', br: 'border-slate-900',  tc: 'text-white',       Icon: FaTiktok,    c: '#fff',    time: '16:30', title: 'Tips' },
    { top: 194, col: 5, bg: 'bg-emerald-50',br: 'border-emerald-200',tc: 'text-emerald-700', Icon: FaThreads,   c: '#000',    time: '17:00', title: 'Thread' },
    { top: 256, col: 2, bg: 'bg-orange-50', br: 'border-orange-200', tc: 'text-orange-700',  Icon: FaInstagram, c: '#E4405F', time: '18:30', title: 'Story' },
  ];

  return (
    <div className="rounded-2xl bg-white shadow-2xl shadow-slate-900/10 border border-slate-200 overflow-hidden h-[500px] flex flex-col">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-900 text-base">Mayo 2024</span>
          <button className="text-slate-400 ml-1"><ChevronLeft size={16} /></button>
          <button className="text-slate-400"><ChevronRight size={16} /></button>
          <span className="text-xs px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 ml-2 font-medium">Hoy</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 font-medium">Semana ▾</span>
          <button className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-brand-600 text-white font-semibold">
            <Plus size={13} /> Nueva
          </button>
        </div>
      </div>

      <div className="grid grid-cols-[56px_repeat(7,1fr)] text-[11px] text-slate-500 border-b border-slate-100">
        <div />
        {days.map((d) => (
          <div key={d.d} className="px-1 py-3 text-center">
            <div className="font-medium tracking-wider">{d.lbl}</div>
            <div
              className={
                d.today
                  ? 'mt-1 mx-auto inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-600 text-white text-[12px] font-bold'
                  : 'mt-1 text-slate-700 font-semibold text-[14px]'
              }
            >
              {d.d}
            </div>
          </div>
        ))}
      </div>

      <div className="relative grid grid-cols-[56px_repeat(7,1fr)] flex-1 overflow-hidden">
        {hours.map((h, i) => (
          <div key={h} className="contents">
            <div className="px-2 py-2 text-slate-400 text-right text-[11px] border-b border-slate-50">{h}</div>
            {[...Array(7)].map((_, j) => (
              <div key={j} className="border-b border-l border-slate-50" data-row={i} />
            ))}
          </div>
        ))}

        {events.map((e, idx) => (
          <div
            key={idx}
            className={`absolute h-12 rounded-md ${e.bg} ${e.br} border ${e.tc} px-2 py-1 text-[10px] leading-tight overflow-hidden shadow-sm ${
              e.drag ? 'animate-calendar-drag z-20' : ''
            }`}
            style={{
              top: `${e.top}px`,
              left: `calc(56px + ${e.col} * ((100% - 56px) / 7) + 3px)`,
              width: `calc((100% - 56px) / 7 - 6px)`,
              transformOrigin: 'center',
            }}
          >
            <div className="flex items-center gap-1 font-bold">
              <e.Icon size={11} color={e.c} />
              {e.time}
            </div>
            <div className="truncate opacity-90 mt-0.5 font-medium">{e.title}</div>
          </div>
        ))}

        {/* virtual cursor that drags the chip above to a different day.
            The wrapper shares the chip's width so its translate3d(200%, …)
            covers the same distance as the chip's transform. */}
        <div
          className="absolute pointer-events-none z-30 animate-calendar-cursor"
          style={{
            top: '132px',
            left: 'calc(56px + 3 * ((100% - 56px) / 7) + 3px)',
            width: 'calc((100% - 56px) / 7 - 6px)',
            height: '48px',
          }}
          aria-hidden
        >
          {/* icon sits near the chip's right edge so it looks like it's
              "grabbing" the chip */}
          <MousePointer2
            size={20}
            strokeWidth={1.5}
            className="absolute fill-white text-slate-900 drop-shadow-md"
            style={{ top: '14px', right: '8px' }}
          />
        </div>
      </div>
    </div>
  );
}

/* ─── CREATE POST card ───────────────────────────────────── */
function CreatePostMockup() {
  const FULL_L1   = 'Eleva tu marca. Conecta con tu audiencia. 🚀';
  const FULL_L2   = 'Contenido que inspira, resultados que crecen.';
  const FULL_TAGS = '#MarketingDigital #Estrategia #Crecimiento';
  const TOTAL     = FULL_L1.length + FULL_L2.length + FULL_TAGS.length;

  // row: 0 = Instagram, 1 = X, 2 = LinkedIn, 3 = Facebook, 4 = TikTok, 5 = YouTube, 6 = Threads
  const nets: { Icon: typeof FaFacebook; color: string }[] = [
    { Icon: FaXTwitter,  color: '#0F1419' },
    { Icon: FaLinkedin,  color: '#0A66C2' },
    { Icon: FaFacebook,  color: '#1877F2' },
    { Icon: FaTiktok,    color: '#000' },
    { Icon: FaYoutube,   color: '#FF0000' },
    { Icon: FaThreads,   color: '#000' },
  ];

  const [chars,     setChars]     = useState(0);
  const [phase,     setPhase]     = useState<'typing' | 'clicking' | 'pause'>('typing');
  const [selected,  setSelected]  = useState<number[]>([0]);   // 0 = Instagram pre-selected
  const [cursorIdx, setCursorIdx] = useState<number | null>(null);

  /* typing */
  useEffect(() => {
    if (phase !== 'typing') return;
    if (chars >= TOTAL) {
      const t = setTimeout(() => setPhase('clicking'), 700);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setChars((c) => c + 1), 30);
    return () => clearTimeout(t);
  }, [phase, chars, TOTAL]);

  /* clicking sequence: cursor moves to X → LinkedIn → Facebook and selects each */
  useEffect(() => {
    if (phase !== 'clicking') return;
    const ts: ReturnType<typeof setTimeout>[] = [];
    ts.push(setTimeout(() => setCursorIdx(1),                                              100));
    ts.push(setTimeout(() => { setSelected((p) => [...p, 1]); setCursorIdx(2); },         600));
    ts.push(setTimeout(() => { setSelected((p) => [...p, 2]); setCursorIdx(3); },        1100));
    ts.push(setTimeout(() => { setSelected((p) => [...p, 3]); setCursorIdx(null); },     1600));
    ts.push(setTimeout(() => setPhase('pause'),                                           2300));
    return () => ts.forEach(clearTimeout);
  }, [phase]);

  /* pause → reset */
  useEffect(() => {
    if (phase !== 'pause') return;
    const t = setTimeout(() => {
      setChars(0);
      setSelected([0]);
      setCursorIdx(null);
      setPhase('typing');
    }, 2000);
    return () => clearTimeout(t);
  }, [phase]);

  /* derived text */
  const l1   = FULL_L1.slice(0, Math.min(chars, FULL_L1.length));
  const l2   = chars > FULL_L1.length
    ? FULL_L2.slice(0, Math.min(chars - FULL_L1.length, FULL_L2.length))
    : '';
  const tags = chars > FULL_L1.length + FULL_L2.length
    ? FULL_TAGS.slice(0, chars - FULL_L1.length - FULL_L2.length)
    : '';
  const activeLine = chars < FULL_L1.length ? 1 : chars < FULL_L1.length + FULL_L2.length ? 2 : 3;

  /* cursor pixel position inside the network row
     px-5 = 20px padding-left · each icon = 36px + gap-2 (8px) = 44px step */
  const cursorLeft = 20 + (cursorIdx ?? 1) * 44 + 28;

  return (
    <div className="rounded-2xl bg-white shadow-2xl shadow-slate-900/10 border border-slate-200 overflow-hidden h-[500px] flex flex-col">
      {/* header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <span className="text-base font-semibold text-slate-900">Crear publicación</span>
        <div className="flex items-center gap-3 text-slate-400">
          <Sparkles size={16} className="text-brand-500" />
          <Maximize2 size={14} />
        </div>
      </div>

      {/* network icons row — cursor is absolutely positioned here */}
      <div className="relative px-5 pt-4 pb-3 flex items-center gap-2 border-b border-slate-50">
        {/* Instagram (idx 0) */}
        <div
          className={`h-9 w-9 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${
            selected.includes(0) ? 'border-brand-500 bg-brand-50' : 'border-slate-200 bg-slate-50'
          }`}
        >
          <FaInstagram size={16} color="#E4405F" />
        </div>
        {/* other nets (idx 1-6) */}
        {nets.map((n, i) => (
          <div
            key={i}
            className={`h-9 w-9 rounded-lg flex items-center justify-center transition-all duration-200 ${
              selected.includes(i + 1)
                ? 'border-2 border-brand-500 bg-brand-50'
                : 'bg-slate-50'
            }`}
          >
            <n.Icon size={16} color={n.color} />
          </div>
        ))}
        <div className="h-9 w-9 rounded-lg bg-slate-50 flex items-center justify-center text-slate-400">
          <Plus size={14} />
        </div>

        {/* virtual cursor that slides between icons */}
        <div
          className="absolute pointer-events-none z-20 transition-all duration-300"
          style={{
            left:    `${cursorLeft}px`,
            top:     '32px',
            opacity: cursorIdx !== null ? 1 : 0,
            transform: cursorIdx !== null ? 'scale(1)' : 'scale(0.5)',
          }}
          aria-hidden
        >
          <MousePointer2
            size={16}
            strokeWidth={1.5}
            className="fill-white text-slate-900 drop-shadow-md"
          />
        </div>
      </div>

      {/* text area with typewriter effect */}
      <div className="px-5 py-4 text-[13px] text-slate-700 leading-relaxed flex-1">
        <div>
          {l1}
          {phase === 'typing' && activeLine === 1 && (
            <span className="inline-block w-[2px] h-[13px] bg-slate-700 ml-px align-middle animate-pulse" />
          )}
        </div>

        {(l2 || activeLine >= 2) && (
          <div className="mt-1">
            {l2}
            {phase === 'typing' && activeLine === 2 && (
              <span className="inline-block w-[2px] h-[13px] bg-slate-700 ml-px align-middle animate-pulse" />
            )}
          </div>
        )}

        {(tags || activeLine >= 3) && (
          <div className="mt-2 text-brand-600 font-semibold">
            {tags}
            {phase === 'typing' && activeLine === 3 && (
              <span className="inline-block w-[2px] h-[13px] bg-brand-600 ml-px align-middle animate-pulse" />
            )}
          </div>
        )}

        <div className="mt-4 flex items-center gap-4 text-slate-400">
          <ImageIcon size={16} />
          <span className="text-[11px] font-bold">GIF</span>
          <Smile size={16} />
          <Hash size={16} />
          <MapPin size={16} />
          <MoreHorizontal size={16} />
        </div>

        <div className="mt-4 rounded-xl overflow-hidden h-32 bg-gradient-to-br from-slate-700 via-slate-600 to-slate-800 relative flex items-center px-5 shadow-inner">
          <div className="text-white">
            <div className="font-extrabold text-2xl leading-tight">Conecta.</div>
            <div className="font-extrabold text-2xl leading-tight">Inspira.</div>
            <div className="font-extrabold text-2xl leading-tight">Crece.</div>
          </div>
          <div className="absolute right-3 bottom-2 text-white/50 text-[11px] font-extrabold">Z</div>
        </div>
      </div>

      {/* footer */}
      <div className="px-5 py-3.5 border-t border-slate-100 flex items-center justify-between text-[11px]">
        <div className="flex items-center gap-2">
          <span className="px-3 py-1.5 rounded-md bg-slate-50 border border-slate-200 text-slate-700 font-medium">Programar ▾</span>
          <span className="px-3 py-1.5 rounded-md bg-slate-50 border border-slate-200 text-slate-700 font-medium">24 may · 10:00</span>
        </div>
        <button className="px-3.5 py-1.5 rounded-md bg-accent-400 text-white font-semibold">Programar ▾</button>
      </div>
    </div>
  );
}

/* ─── MULTICHANNEL card ──────────────────────────────────── */
function MultichannelMockup() {
  const nets: { Icon: typeof FaFacebook; color: string; name: string; followers: string }[] = [
    { Icon: FaInstagram, color: '#E4405F', name: 'Instagram', followers: '12.4K' },
    { Icon: FaXTwitter,  color: '#0F1419', name: 'X',         followers: '8.1K' },
    { Icon: FaLinkedin,  color: '#0A66C2', name: 'LinkedIn',  followers: '4.7K' },
    { Icon: FaFacebook,  color: '#1877F2', name: 'Facebook',  followers: '9.2K' },
    { Icon: FaTiktok,    color: '#000',    name: 'TikTok',    followers: '21.0K' },
    { Icon: FaYoutube,   color: '#FF0000', name: 'YouTube',   followers: '3.4K' },
    { Icon: FaThreads,   color: '#000',    name: 'Threads',   followers: '2.1K' },
    { Icon: FaPinterest, color: '#BD081C', name: 'Pinterest', followers: '1.8K' },
  ];
  return (
    <div className="rounded-2xl bg-white shadow-2xl shadow-slate-900/10 border border-slate-200 overflow-hidden h-[500px] flex flex-col p-5">
      <div>
        <div className="text-base font-semibold text-slate-900">Publicación multicanal</div>
        <div className="mt-1 text-sm text-slate-500">Un contenido. Todas tus redes a la vez.</div>
      </div>

      <div className="mt-5 grid grid-cols-4 gap-2.5 flex-1">
        {nets.map((n, i) => (
          <div
            key={i}
            className="relative rounded-xl border border-slate-200 bg-slate-50/40 p-3 flex flex-col items-center justify-center gap-1.5 hover:bg-slate-50 transition"
          >
            <div className="h-10 w-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
              <n.Icon size={20} color={n.color} />
            </div>
            <span className="text-[11px] font-semibold text-slate-800">{n.name}</span>
            <span className="text-[10px] text-slate-500">{n.followers}</span>
            <span className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center">
              <Check size={9} strokeWidth={4} className="text-white" />
            </span>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
          <Check size={20} strokeWidth={3} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-emerald-800">Listo para publicar</div>
          <div className="text-xs text-emerald-700 mt-0.5">24 may 2024 · 10:00 · 8 redes seleccionadas</div>
        </div>
        <button className="text-xs px-3 py-1.5 rounded-md bg-white text-emerald-700 font-semibold border border-emerald-200 hover:bg-emerald-50">
          Publicar
        </button>
      </div>
    </div>
  );
}

/* ─── APPROVALS card ─────────────────────────────────────── */
function ApprovalsMockup() {
  const items = [
    { name: 'Lanzamiento de producto',  who: 'Andrea Ruiz · Marketing',  initial: 'A', color: 'from-pink-300 to-pink-500',   approved: false },
    { name: 'Campaña de verano',        who: 'Carlos Marín · Diseño',    initial: 'C', color: 'from-blue-300 to-blue-500',   approved: false },
    { name: 'Nuevo post en el blog',    who: 'Sofía Vidal · Contenido',  initial: 'S', color: 'from-emerald-300 to-emerald-500', approved: true },
    { name: 'Hilo en X · IA & SaaS',    who: 'Andrea Ruiz · Marketing',  initial: 'A', color: 'from-pink-300 to-pink-500',   approved: false },
    { name: 'Vídeo en YouTube',         who: 'Marcos Pérez · Vídeo',     initial: 'M', color: 'from-amber-300 to-amber-500', approved: false },
  ];
  return (
    <div className="rounded-2xl bg-white shadow-2xl shadow-slate-900/10 border border-slate-200 overflow-hidden h-[500px] flex flex-col p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-base font-semibold text-slate-900">Aprobaciones</div>
          <div className="text-xs text-slate-500 mt-0.5">Tu equipo revisa antes de publicar</div>
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
          <Clock size={11} /> 4 pendientes
        </span>
      </div>

      <div className="mt-4 flex gap-5 text-sm border-b border-slate-100">
        <span className="text-slate-900 font-semibold border-b-2 border-brand-600 pb-2.5">
          Pendientes <span className="text-brand-600">4</span>
        </span>
        <span className="text-slate-400 pb-2.5">Aprobadas <span className="text-slate-400">12</span></span>
        <span className="text-slate-400 pb-2.5">Rechazadas</span>
      </div>

      <div className="mt-3 space-y-2 flex-1 overflow-hidden">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50/50 p-2.5 transition">
            <div className={`h-9 w-9 rounded-full bg-gradient-to-br ${it.color} flex items-center justify-center text-white text-sm font-bold shrink-0`}>
              {it.initial}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-slate-900 truncate">{it.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{it.who}</div>
            </div>
            <span
              className={`text-[10px] px-2 py-1 rounded-full font-semibold border whitespace-nowrap ${
                it.approved
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}
            >
              {it.approved ? 'Aprobada' : 'Pendiente'}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-slate-500">3 revisores activos</span>
        <span className="text-brand-600 font-semibold cursor-pointer hover:underline">Ver todas →</span>
      </div>
    </div>
  );
}

/* ─── QUEUE card ─────────────────────────────────────────── */
function QueueMockup() {
  const items = [
    { Icon: FaInstagram, title: 'Consejos para tu marca',     date: 'Hoy · 10:00',    bg: 'from-pink-400 to-rose-500' },
    { Icon: FaXTwitter,  title: 'Hilo: cómo escalar SaaS',    date: 'Hoy · 12:00',    bg: 'from-slate-700 to-slate-900' },
    { Icon: FaLinkedin,  title: 'Estrategia que sí funciona', date: 'Mañana · 09:30', bg: 'from-blue-400 to-blue-600' },
    { Icon: FaFacebook,  title: 'Historias que inspiran',     date: 'Mañana · 14:00', bg: 'from-sky-400 to-blue-500' },
    { Icon: FaYoutube,   title: 'Vídeo: tour del producto',   date: '25 may · 11:00', bg: 'from-red-400 to-red-600' },
    { Icon: FaTiktok,    title: 'Reel: 60s de productividad', date: '25 may · 16:30', bg: 'from-fuchsia-400 to-fuchsia-600' },
  ];
  return (
    <div className="rounded-2xl bg-white shadow-2xl shadow-slate-900/10 border border-slate-200 overflow-hidden h-[500px] flex flex-col p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-base font-semibold text-slate-900">Cola de publicaciones</div>
          <div className="text-xs text-slate-500 mt-0.5">Lo que sale hoy, mañana y los próximos días</div>
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 border border-brand-200 font-semibold">
          {items.length} en cola
        </span>
      </div>

      <div className="mt-4 space-y-2 flex-1 overflow-hidden">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-100 p-2.5 hover:border-slate-200 hover:bg-slate-50/50 transition">
            <div className={`h-11 w-11 rounded-lg bg-gradient-to-br ${it.bg} flex items-center justify-center shrink-0 shadow-sm`}>
              <it.Icon size={18} color="#fff" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-slate-900 truncate">{it.title}</div>
              <div className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                <Clock size={10} /> {it.date}
              </div>
            </div>
            <button className="text-slate-300 hover:text-slate-600">
              <MoreHorizontal size={16} />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-slate-500">Próxima publicación en 2h</span>
        <span className="text-brand-600 font-semibold cursor-pointer hover:underline">Ver toda la cola →</span>
      </div>
    </div>
  );
}

/* ─── PERFORMANCE card ───────────────────────────────────── */
export function PerformanceMockup() {
  const metrics = [
    { label: 'Alcance',         value: '124.8K', delta: '+24%', bar: 'from-brand-300 to-brand-600' },
    { label: 'Interacciones',   value: '9.3K',   delta: '+18%', bar: 'from-emerald-300 to-emerald-600' },
    { label: 'Clics en enlace', value: '2.1K',   delta: '+32%', bar: 'from-violet-300 to-violet-600' },
    { label: 'Publicaciones',   value: '56',     delta: '+12%', bar: 'from-amber-300 to-amber-600' },
  ];
  // tiny bar chart values (last 14 days)
  const bars = [38, 52, 30, 65, 48, 78, 56, 88, 70, 95, 82, 72, 90, 100];
  return (
    <div className="rounded-2xl bg-white shadow-2xl shadow-slate-900/10 border border-slate-200 overflow-hidden h-[500px] flex flex-col p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-base font-semibold text-slate-900">Rendimiento</div>
          <div className="text-xs text-slate-500 mt-0.5">Todas tus redes · resumen</div>
        </div>
        <span className="text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 font-medium">Últimos 30 días ▾</span>
      </div>

      {/* mini bar chart */}
      <div className="mt-4 rounded-xl bg-slate-50/70 border border-slate-100 p-4">
        <div className="flex items-center justify-between text-xs mb-3">
          <span className="text-slate-700 font-medium">Alcance diario</span>
          <span className="inline-flex items-center gap-1 text-emerald-600 font-bold">
            <TrendingUp size={12} /> +24%
          </span>
        </div>
        <div className="flex items-end gap-1.5 h-24">
          {bars.map((b, i) => (
            <div
              key={i}
              className={`flex-1 rounded-t bg-gradient-to-t from-brand-500 to-brand-300 transition-opacity`}
              style={{ height: `${b}%`, opacity: i >= bars.length - 3 ? 1 : 0.7 }}
            />
          ))}
        </div>
      </div>

      {/* metrics grid */}
      <div className="mt-4 grid grid-cols-2 gap-3 flex-1">
        {metrics.map((m) => (
          <div key={m.label} className="rounded-xl border border-slate-200 p-3.5 flex flex-col justify-between">
            <div className="text-[11px] text-slate-500 font-medium">{m.label}</div>
            <div className="flex items-end justify-between mt-1">
              <div className="text-2xl font-extrabold text-slate-900 leading-none">{m.value}</div>
              <div className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-bold">
                <TrendingUp size={12} /> {m.delta}
              </div>
            </div>
            <div className={`mt-2.5 h-1.5 rounded-full bg-gradient-to-r ${m.bar}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   How-it-works animated mockups
   ─────────────────────────────────────────────────────────── */

function HowItWorksStep({ number, title, body, Mockup }: {
  number: string;
  title: string;
  body: string;
  Mockup: React.ComponentType;
}) {
  return (
    <div className="flex flex-col">
      <div className="relative rounded-3xl bg-gradient-to-br from-slate-50 via-white to-white border border-slate-200 shadow-sm overflow-hidden h-80 p-5">
        {/* very subtle dotted backdrop */}
        <div
          className="absolute inset-0 -z-0 opacity-50 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgb(226 232 240) 1px, transparent 1px)',
            backgroundSize: '18px 18px',
          }}
        />
        <div className="relative h-full">
          <Mockup />
        </div>
      </div>
      <div className="mt-7 flex items-start gap-4 px-1">
        <div className="font-bold text-slate-400 tabular-nums text-sm tracking-wide pt-1">{number}</div>
        <div className="min-w-0">
          <h3 className="font-bold text-xl text-slate-900 tracking-tight">{title}</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
        </div>
      </div>
    </div>
  );
}

/* ── 01 — Upload mockup ───────────────────────────────────── */

const TILE_IMAGES = [
  'https://zettapost-media-prod.s3.us-east-1.amazonaws.com/users/fca50187-dbb0-4a99-89d2-e23aff34be18/6d02f47c-7729-4c2a-a89e-bf689363d4cd.jpg',
  'https://zettapost-media-prod.s3.us-east-1.amazonaws.com/users/fca50187-dbb0-4a99-89d2-e23aff34be18/1704f4fc-8d23-4722-b7d4-37a268fef68b.jpg',
  'https://zettapost-media-prod.s3.us-east-1.amazonaws.com/users/fca50187-dbb0-4a99-89d2-e23aff34be18/50d32019-5c35-4c21-aca8-bf6a7f7f9a4c.jpg',
  'https://zettapost-media-prod.s3.us-east-1.amazonaws.com/users/fca50187-dbb0-4a99-89d2-e23aff34be18/c554af34-a256-4e5a-bbc8-3122e98bc68c.jpg',
  'https://zettapost-media-prod.s3.us-east-1.amazonaws.com/users/fca50187-dbb0-4a99-89d2-e23aff34be18/d2c1454f-3073-4bd2-926b-fb1f0894c878.jpg',
  'https://zettapost-media-prod.s3.us-east-1.amazonaws.com/users/fca50187-dbb0-4a99-89d2-e23aff34be18/10178374-aed9-4231-bd63-fffa5cb20790.jpg',
];

function UploadMockup() {
  return (
    <div className="relative flex h-full flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-md bg-brand-100 text-brand-600 flex items-center justify-center">
            <ImageIcon size={12} />
          </div>
          <span className="text-xs font-semibold text-slate-800">Tu biblioteca</span>
        </div>
        <span className="text-[10px] text-slate-500 font-medium">6 archivos</span>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        {TILE_IMAGES.map((src, i) => (
          <div
            key={i}
            className="aspect-square rounded-lg bg-slate-100 animate-howto-tile shadow-sm relative overflow-hidden"
            style={{ animationDelay: `${0.1 + i * 0.18}s` }}
          >
            <img
              src={src}
              alt=""
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
            {i === 1 && (
              <div className="absolute inset-0 flex items-center justify-center text-white drop-shadow">
                <Film size={14} />
              </div>
            )}
            {i === 4 && (
              <div className="absolute bottom-1 right-1 text-[8px] font-bold text-white/90 bg-black/40 px-1 rounded">0:14</div>
            )}
          </div>
        ))}
      </div>

      {/* Drop zone */}
      <div className="rounded-xl py-4 text-center animate-howto-zone relative">
        <div className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-brand-600 mb-1">
          <Upload size={14} />
        </div>
        <p className="text-[11px] text-slate-600 font-medium">Arrastra aquí tus archivos</p>
        <p className="text-[10px] text-slate-400">o haz clic para seleccionarlos</p>
      </div>

      {/* The dropping file */}
      <div className="absolute top-10 right-2 animate-howto-drop pointer-events-none">
        <div className="w-16 h-16 rounded-xl shadow-xl border-2 border-white bg-gradient-to-br from-pink-300 via-rose-300 to-orange-300 flex items-center justify-center relative">
          <ImageIcon size={20} className="text-white drop-shadow" />
          <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow">
            <Plus size={10} className="text-brand-600" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── 02 — Composer + AI score mockup ─────────────────────── */

const AI_CAPTION_WORDS = [
  'Estrenamos', 'nuestra', 'nueva', 'colección', 'de', 'primavera',
  '🌸', '·', 'algodón', 'orgánico,', 'cero', 'plástico.',
];

function ComposerMockup() {
  // Ring math (radius 22, stroke 5)
  const R = 22;
  const CIRC = 2 * Math.PI * R;
  const TARGET = CIRC * (1 - 0.87); // 87%

  return (
    <div className="relative flex h-full flex-col">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-800">Texto de la publicación</span>
        <span className="text-[10px] text-slate-400">Instagram · Reel</span>
      </div>

      {/* Textarea-like with AI typewriter */}
      <div className="rounded-xl border border-slate-200 bg-white p-3 mb-2.5 min-h-[78px]">
        <p className="text-[13px] text-slate-900 leading-relaxed">
          {AI_CAPTION_WORDS.map((w, i) => (
            <span
              key={i}
              className="inline-block animate-howto-word"
              style={{ animationDelay: `${i * 0.12}s` }}
            >
              {w}{' '}
            </span>
          ))}
          <span className="inline-block w-[1.5px] h-3.5 bg-brand-600 align-middle ml-px animate-howto-caret" />
        </p>
      </div>

      {/* AI button */}
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gradient-to-br from-brand-500 to-purple-600 text-white text-[11px] font-semibold shadow shadow-brand-500/20"
        >
          <Sparkles size={11} /> Generar con IA
        </button>
        <span className="text-[10px] text-slate-500">o escribe tú mismo</span>
      </div>

      {/* AI score card */}
      <div className="mt-auto rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-3 flex items-center gap-3">
        <div className="relative flex-none" style={{ width: 54, height: 54 }}>
          <svg width={54} height={54} viewBox="0 0 54 54" className="-rotate-90">
            <circle cx="27" cy="27" r={R} stroke="rgb(220 252 231)" strokeWidth="5" fill="none" />
            <circle
              cx="27" cy="27" r={R}
              stroke="rgb(16 185 129)" strokeWidth="5" fill="none"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              className="animate-howto-ring"
              style={{
                ['--ring-circ' as string]: `${CIRC}`,
                ['--ring-target' as string]: `${TARGET}`,
                strokeDashoffset: CIRC,
              } as React.CSSProperties}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-base font-extrabold text-emerald-700 animate-howto-score-num tabular-nums">87</span>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Sparkles size={11} className="text-emerald-600" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">Predicción IA</span>
          </div>
          <div className="text-[13px] font-semibold text-slate-900 mt-0.5">Excelente rendimiento</div>
          <p className="text-[11px] text-slate-600 mt-0.5 leading-snug">Hook fuerte, CTA implícito y hashtags adecuados.</p>
        </div>
      </div>
    </div>
  );
}

/* ── 03 — Networks pick mockup ───────────────────────────── */

const MOCKUP_ACCOUNTS = [
  { Icon: FaInstagram, color: '#E4405F', name: 'Instagram', handle: '@tu_marca' },
  { Icon: FaXTwitter,  color: '#0F1419', name: 'X',         handle: '@tu_marca' },
  { Icon: FaLinkedin,  color: '#0A66C2', name: 'LinkedIn',  handle: 'Tu Marca' },
  { Icon: FaTiktok,    color: '#000000', name: 'TikTok',    handle: '@tu.marca' },
];

function NetworksMockup() {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-slate-800">Publicar en</span>
        <span className="text-[10px] text-slate-500 font-medium">4 seleccionadas</span>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        {MOCKUP_ACCOUNTS.map((a, i) => (
          <div
            key={i}
            className="relative rounded-xl border border-slate-200 p-2.5 flex items-center gap-2 animate-howto-account animate-howto-selected"
            style={{ animationDelay: `${0.2 + i * 0.25}s` }}
          >
            <div
              className="h-8 w-8 rounded-full flex items-center justify-center flex-none"
              style={{ background: `${a.color}15` }}
            >
              <a.Icon size={15} color={a.color} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-slate-900 truncate leading-tight">{a.name}</div>
              <div className="text-[10px] text-slate-500 truncate">{a.handle}</div>
            </div>
            <div
              className="absolute -top-1.5 -right-1.5 h-4.5 w-4.5 rounded-full bg-brand-600 text-white flex items-center justify-center shadow animate-howto-check"
              style={{ animationDelay: `${0.45 + i * 0.25}s`, width: 18, height: 18 }}
            >
              <Check size={11} strokeWidth={3} />
            </div>
          </div>
        ))}
      </div>

      {/* Schedule chip */}
      <div className="mt-auto rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center flex-none">
          <CalendarDays size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">Programar</div>
          <div className="text-[12px] font-semibold text-slate-900">Mañana, 10:00</div>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-brand-600 text-white text-[11px] font-semibold"
        >
          Publicar <ArrowRight size={11} />
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Testimonial card
   ─────────────────────────────────────────────────────────── */

function TestimonialCard({ quote, name, role, initials, gradient }: Testimonial) {
  return (
    <div className="shrink-0 w-[340px] sm:w-[380px] rounded-2xl bg-white border border-slate-200 shadow-sm p-6 flex flex-col gap-5">
      {/* quote mark */}
      <div className="text-5xl leading-none text-brand-100 font-serif select-none -mb-3">“</div>
      <p className="text-slate-700 text-sm sm:text-base leading-relaxed">
        {quote}
      </p>
      <div className="mt-auto pt-4 border-t border-slate-100 flex items-center gap-3">
        <div
          className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${gradient} text-white text-xs font-bold shadow-sm`}
        >
          {initials}
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 text-sm truncate">{name}</div>
          <div className="text-xs text-slate-500 truncate">{role}</div>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────
   Feature cards — bento mockups
   ─────────────────────────────────────────────────────────── */

function FeatureCard({ eyebrow, title, body, Mockup, wide, full, badge }: {
  eyebrow: string;
  title: string;
  body: string;
  Mockup: React.ComponentType;
  wide?: boolean;
  full?: boolean;
  badge?: string;
}) {
  return (
    <div
      className={
        'group relative rounded-3xl bg-white border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden flex flex-col ' +
        (full ? 'lg:col-span-3' : wide ? 'lg:col-span-2' : '')
      }
    >
      <div className="relative h-48 sm:h-52 overflow-hidden bg-gradient-to-br from-slate-50 via-white to-white border-b border-slate-100">
        {/* dot grid */}
        <div
          aria-hidden
          className="absolute inset-0 -z-0 opacity-50 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgb(226 232 240) 1px, transparent 1px)',
            backgroundSize: '16px 16px',
          }}
        />
        <div className="relative h-full p-4 sm:p-5">
          <Mockup />
        </div>
      </div>
      <div className="p-5 sm:p-6 flex-1">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-brand-600">{eyebrow}</span>
          {badge && (
            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-gradient-to-r from-accent-400 to-accent-500 text-white">
              {badge}
            </span>
          )}
        </div>
        <h3 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight tracking-tight">{title}</h3>
        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

/* ── 1) Multi-network ──────────────────────────────────── */

const FEAT_NETWORKS = [
  { Icon: FaInstagram, color: '#E4405F' },
  { Icon: FaXTwitter,  color: '#0F1419' },
  { Icon: FaLinkedin,  color: '#0A66C2' },
  { Icon: FaFacebook,  color: '#1877F2' },
  { Icon: FaTiktok,    color: '#000000' },
  { Icon: FaYoutube,   color: '#FF0000' },
  { Icon: FaThreads,   color: '#000000' },
  { Icon: FaPinterest, color: '#BD081C' },
  { Icon: FaBluesky,   color: '#0085FF' },
];

export function MultiNetworkMockup() {
  return (
    <div className="relative flex h-full items-center">
      {/* Compose card */}
      <div className="relative rounded-xl border border-slate-200 bg-white shadow-md p-3 w-44 flex-none">
        <div className="flex items-center gap-1.5 mb-1.5">
          <div className="h-5 w-5 rounded bg-brand-100 text-brand-600 flex items-center justify-center">
            <Send size={10} />
          </div>
          <span className="text-[10px] font-semibold text-slate-700">Tu publicación</span>
        </div>
        <div className="text-[11px] text-slate-800 leading-snug line-clamp-3">
          Estrenamos colección de primavera. Algodón orgánico, cero plástico. 🌸
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[9px] text-slate-500">14:32</span>
          <button className="text-[9px] px-2 py-0.5 rounded bg-brand-600 text-white font-semibold">Publicar</button>
        </div>
      </div>

      {/* Flow lines (decorative) */}
      <div className="hidden sm:block flex-1 mx-3 relative h-24">
        <svg viewBox="0 0 100 60" preserveAspectRatio="none" className="absolute inset-0 w-full h-full text-slate-300">
          <path d="M0 30 Q 50 0 100 5" stroke="currentColor" strokeWidth="0.6" fill="none" strokeDasharray="1.5 1.5" />
          <path d="M0 30 Q 50 30 100 30" stroke="currentColor" strokeWidth="0.6" fill="none" strokeDasharray="1.5 1.5" />
          <path d="M0 30 Q 50 60 100 55" stroke="currentColor" strokeWidth="0.6" fill="none" strokeDasharray="1.5 1.5" />
        </svg>
      </div>

      {/* Platform chip grid */}
      <div className="grid grid-cols-3 gap-1.5 flex-none ml-auto">
        {FEAT_NETWORKS.map((n, i) => (
          <div
            key={i}
            className="h-9 w-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center shadow-sm animate-feat-net-pulse"
            style={{ animationDelay: `${i * 0.45}s` }}
          >
            <n.Icon size={15} color={n.color} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 2) AI Performance ──────────────────────────────────── */

export function AiFeatureMockup() {
  const R = 24;
  const CIRC = 2 * Math.PI * R;
  const TARGET = CIRC * (1 - 0.87);

  const tips = [
    'Añade un CTA al final',
    'Usa 1–2 hashtags relevantes',
    'Mueve el hook al inicio',
  ];

  return (
    <div className="relative h-full flex flex-col">
      {/* score row */}
      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-none" style={{ width: 60, height: 60 }}>
          <svg width={60} height={60} viewBox="0 0 60 60" className="-rotate-90">
            <circle cx="30" cy="30" r={R} stroke="rgb(220 252 231)" strokeWidth="5" fill="none" />
            <circle
              cx="30" cy="30" r={R}
              stroke="rgb(16 185 129)" strokeWidth="5" fill="none"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              className="animate-howto-ring"
              style={{
                ['--ring-circ' as string]: `${CIRC}`,
                ['--ring-target' as string]: `${TARGET}`,
                strokeDashoffset: CIRC,
              } as React.CSSProperties}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-extrabold text-emerald-700 animate-howto-score-num tabular-nums">87</span>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Sparkles size={11} className="text-emerald-600" />
            <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700">Predicción</span>
          </div>
          <div className="text-sm font-bold text-slate-900 mt-0.5">Excelente</div>
          <p className="text-[11px] text-slate-500 leading-snug">100% con tu marca y audiencia.</p>
        </div>
      </div>

      {/* tips */}
      <div className="space-y-1.5">
        {tips.map((t, i) => (
          <div
            key={i}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 flex items-center gap-2 animate-howto-account"
            style={{ animationDelay: `${0.6 + i * 0.25}s` }}
          >
            <span className="h-5 w-5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold flex items-center justify-center flex-none">
              {i + 1}
            </span>
            <span className="text-[11px] text-slate-700 truncate">{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── 3) Calendar drag&drop ──────────────────────────────── */

export function CalendarFeatureMockup() {
  const days = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
  return (
    <div className="relative h-full flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold text-slate-700">Mayo 2026</span>
        <div className="flex items-center gap-1 text-slate-400">
          <ChevronLeft size={12} />
          <ChevronRight size={12} />
        </div>
      </div>

      {/* day labels */}
      <div className="grid grid-cols-4 gap-1 mb-1.5 text-center">
        {days.slice(0, 4).map((d) => (
          <div key={d} className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold">{d}</div>
        ))}
      </div>

      {/* mini calendar — 3 rows × 4 cols */}
      <div className="grid grid-cols-4 gap-1 flex-1 relative">
        {Array.from({ length: 12 }).map((_, i) => {
          const day = i + 12; // 12, 13, ...
          const hasPost = [1, 3, 5, 8, 9].includes(i);
          return (
            <div
              key={i}
              className="relative rounded-md bg-slate-50 border border-slate-100 p-1 min-h-[34px]"
            >
              <div className="text-[8px] text-slate-400 font-medium">{day}</div>
              {/* The dragged chip is anchored to cell 5 (i=5). */}
              {hasPost && i !== 5 && (
                <div className="mt-0.5 h-1.5 rounded bg-brand-200" />
              )}
            </div>
          );
        })}

        {/* Dragged chip — sits over cell index 5 (second row, second col) */}
        <div
          className="absolute pointer-events-none"
          style={{
            // 4 cols, cell width = (100% - 3 gaps) / 4; gap = 4px (gap-1)
            left: 'calc(((100% - 12px) / 4) * 1 + 4px)',
            top: 'calc((100% / 3) + 4px)',
            width: 'calc((100% - 12px) / 4)',
            height: 'calc((100% / 3) - 8px)',
          }}
        >
          <div className="h-full w-full rounded-md bg-white border border-brand-300 shadow flex items-center gap-1 px-1 animate-feat-cal-drag">
            <div className="h-3 w-3 rounded-full bg-brand-500 flex-none" />
            <span className="text-[8px] font-medium text-slate-700 truncate">Reel</span>
          </div>
        </div>

        {/* Cursor */}
        <div
          className="absolute pointer-events-none text-brand-600 animate-calendar-cursor"
          style={{
            left: 'calc(((100% - 12px) / 4) * 1 + 4px)',
            top: 'calc((100% / 3) + 14px)',
          }}
        >
          <MousePointer2 size={12} className="drop-shadow" />
        </div>
      </div>
    </div>
  );
}

/* ── 4) Queue slots ─────────────────────────────────────── */

export function QueueFeatureMockup() {
  const slots = [
    { day: 'Lun', time: '09:00' },
    { day: 'Mié', time: '12:00' },
    { day: 'Vie', time: '18:30' },
    { day: 'Sáb', time: '11:00' },
  ];
  return (
    <div className="relative h-full flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-6 w-6 rounded-md bg-brand-100 text-brand-600 flex items-center justify-center">
          <Clock size={12} />
        </div>
        <span className="text-[11px] font-semibold text-slate-800">Tu cola semanal</span>
      </div>

      <div className="space-y-1.5 flex-1">
        {slots.map((s, i) => (
          <div
            key={i}
            className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 animate-feat-queue-slot"
            style={{ animationDelay: `${i * 1.2}s` }}
          >
            <span className="text-[10px] font-bold uppercase tracking-wider w-7 text-current">{s.day}</span>
            <div className="flex-1 h-px bg-current opacity-20" />
            <span className="text-[11px] font-semibold tabular-nums text-current">{s.time}</span>
          </div>
        ))}
      </div>

      <div className="mt-3 rounded-lg border border-dashed border-brand-200 bg-brand-50/50 px-2.5 py-1.5 text-[10px] text-brand-700 font-medium">
        Siguiente publicación: <span className="font-bold">Lun · 09:00</span>
      </div>
    </div>
  );
}

/* ── 5) Formats ─────────────────────────────────────────── */

export function FormatsFeatureMockup() {
  const formats = [
    { key: 'post',  label: 'Post',  Icon: ImageIcon, color: '#016fff' },
    { key: 'reel',  label: 'Reel',  Icon: Film,      color: '#E4405F' },
    { key: 'story', label: 'Story', Icon: Zap,       color: '#FF8A3D' },
    { key: 'pin',   label: 'Pin',   Icon: MapPin,    color: '#BD081C' },
  ];
  return (
    <div className="relative h-full flex flex-col">
      <span className="text-[10px] font-semibold text-slate-700 mb-2">Elige el formato</span>

      {/* Format tabs */}
      <div className="grid grid-cols-2 gap-1.5 mb-3">
        {formats.map((f, i) => (
          <div
            key={f.key}
            className="relative rounded-lg border border-slate-200 bg-white px-2 py-2 flex items-center gap-1.5 overflow-hidden"
          >
            <div
              className="absolute inset-0 rounded-lg animate-feat-active"
              style={{ background: `${f.color}10`, boxShadow: `inset 0 0 0 1.5px ${f.color}`, animationDelay: `${i * 1.5}s` }}
            />
            <div
              className="h-6 w-6 rounded-md flex items-center justify-center flex-none relative"
              style={{ background: `${f.color}18`, color: f.color }}
            >
              <f.Icon size={12} />
            </div>
            <span className="text-[11px] font-semibold text-slate-800 relative">{f.label}</span>
          </div>
        ))}
      </div>

      {/* Aspect ratio guide */}
      <div className="mt-auto rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-2 flex items-center gap-2">
        <Layers size={12} className="text-slate-500 flex-none" />
        <span className="text-[10px] text-slate-600">9:16 · 1:1 · 4:5 · 16:9</span>
      </div>
    </div>
  );
}

/* ── 6) Previews ────────────────────────────────────────── */

export function PreviewsFeatureMockup() {
  return (
    <div className="relative flex h-full items-center gap-2 sm:gap-3">
      {/* Mini IG-like card */}
      <div className="relative flex-1 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-slate-100">
          <div className="h-5 w-5 rounded-full bg-gradient-to-br from-pink-400 via-rose-400 to-orange-300" />
          <span className="text-[9px] font-semibold text-slate-700 truncate">tu_marca</span>
          <FaInstagram size={10} color="#E4405F" className="ml-auto" />
        </div>
        <div className="aspect-square bg-slate-100 overflow-hidden">
          <img
            src="https://zettapost-media-prod.s3.us-east-1.amazonaws.com/users/fca50187-dbb0-4a99-89d2-e23aff34be18/84815f4f-e9b4-407a-b907-d0cb320bc3df.jpg"
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="px-2 py-1.5 flex items-center gap-2.5 text-slate-600">
          <Heart size={11} />
          <MessageSquare size={11} />
          <Send size={11} />
        </div>
      </div>

      {/* Mini X-like card */}
      <div className="relative flex-1 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-slate-100">
          <div className="h-5 w-5 rounded-full bg-slate-900" />
          <span className="text-[9px] font-semibold text-slate-700 truncate">tu_marca</span>
          <FaXTwitter size={10} color="#0F1419" className="ml-auto" />
        </div>
        <div className="p-2 text-[10px] text-slate-800 leading-snug line-clamp-4">
          Estrenamos colección 🌸 Algodón orgánico, cero plástico. Hecha en España.
        </div>
        <div className="px-2 pb-1.5 flex items-center gap-2.5 text-slate-500">
          <MessageSquare size={10} />
          <Heart size={10} />
          <BarChart3 size={10} />
        </div>
      </div>

      {/* Mini LinkedIn-like card */}
      <div className="relative flex-1 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden hidden sm:block">
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-slate-100">
          <div className="h-5 w-5 rounded-full bg-gradient-to-br from-sky-400 to-blue-600" />
          <span className="text-[9px] font-semibold text-slate-700 truncate">Tu Marca</span>
          <FaLinkedin size={10} color="#0A66C2" className="ml-auto" />
        </div>
        <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
          <img
            src="https://zettapost-media-prod.s3.us-east-1.amazonaws.com/users/fca50187-dbb0-4a99-89d2-e23aff34be18/4d8b16eb-29cc-4f39-b1ea-0b9cc04483a9.jpg"
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="px-2 py-1.5 text-[9px] text-slate-500">87 reacciones · 12 comentarios</div>
      </div>
    </div>
  );
}

/* ── 7) Brand profiles ──────────────────────────────────── */

export function BrandFeatureMockup() {
  const palette = ['#016fff', '#FF8A3D', '#10B981', '#8B5CF6', '#0F172A'];
  return (
    <div className="relative h-full flex flex-col">
      <div className="rounded-xl border border-slate-200 bg-white p-3 mb-2.5">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-brand-500 to-purple-600 flex items-center justify-center text-white">
            <Palette size={15} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold text-slate-900 truncate">Mi Marca · 🌸</div>
            <div className="text-[9px] text-slate-500">Tono cercano, profesional</div>
          </div>
        </div>

        <div className="mt-2 flex items-center gap-1">
          {palette.map((c, i) => (
            <div
              key={i}
              className="h-5 w-5 rounded-full border border-white shadow-sm animate-feat-pop"
              style={{ background: c, animationDelay: `${0.3 + i * 0.12}s` }}
            />
          ))}
        </div>
      </div>

      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Cuentas vinculadas</div>
      <div className="flex gap-1.5">
        {[FaInstagram, FaXTwitter, FaLinkedin, FaTiktok].map((Icon, i) => (
          <div
            key={i}
            className="h-7 w-7 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center animate-feat-pop"
            style={{ animationDelay: `${0.8 + i * 0.1}s` }}
          >
            <Icon size={12} className="text-slate-700" />
          </div>
        ))}
        <div
          className="h-7 w-7 rounded-full bg-brand-50 border border-dashed border-brand-300 text-brand-600 flex items-center justify-center animate-feat-pop"
          style={{ animationDelay: '1.2s' }}
        >
          <Plus size={11} />
        </div>
      </div>
    </div>
  );
}

/* ── Link in bio ────────────────────────────────────────── */

export function LinkInBioFeatureMockup() {
  const links = [
    { Icon: FaInstagram, label: 'Mi Instagram',  color: '#E4405F' },
    { Icon: Globe,       label: 'mi-tienda.com', color: '#016fff' },
    { Icon: FaYoutube,   label: 'Canal YouTube', color: '#FF0000' },
  ];
  const socials = [FaInstagram, FaTiktok, FaXTwitter, FaYoutube];
  return (
    <div className="relative h-full flex items-center justify-center gap-4">
      {/* Phone preview */}
      <div className="relative animate-feat-bio-bob">
        <div
          className="rounded-[22px] border-[6px] border-slate-200 bg-white overflow-hidden h-44 w-28 relative shadow-md"
          style={{ background: 'linear-gradient(135deg, #a78bfa 0%, #f0abfc 100%)' }}
        >
          <div className="absolute inset-0 px-3 pt-3 text-center">
            {/* Avatar */}
            <div className="mx-auto h-9 w-9 rounded-full bg-white/35 ring-2 ring-white/60" />
            <div className="mt-1.5 text-[9px] font-bold text-white tracking-tight">@tu-marca</div>
            {/* Social row */}
            <div className="mt-1.5 flex justify-center gap-1">
              {socials.map((Icon, i) => (
                <span
                  key={i}
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/25 text-white"
                >
                  <Icon size={8} />
                </span>
              ))}
            </div>
            {/* Link buttons (sequential tap) */}
            <div className="mt-2 space-y-1.5">
              {links.map((l, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 rounded-md bg-white/90 text-slate-900 text-[8px] font-semibold px-1.5 py-1 animate-feat-bio-tap"
                  style={{ animationDelay: `${i * 1.5}s` }}
                >
                  <l.Icon size={8} color={l.color} className="shrink-0" />
                  <span className="truncate flex-1 text-left">{l.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right column: URL chip + analytics chip */}
      <div className="hidden sm:flex flex-col gap-2">
        <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm flex items-center gap-1.5">
          <AtSign size={11} className="text-brand-600 shrink-0" />
          <span className="text-[10px] font-mono text-slate-700">zlink.club/tu-marca</span>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm">
          <div className="text-[8px] uppercase tracking-wider font-bold text-slate-400">Visitas hoy</div>
          <div className="text-[14px] font-bold text-slate-900 leading-tight">+312</div>
          <div className="text-[8px] text-emerald-600 font-semibold">+24% vs ayer</div>
        </div>
      </div>
    </div>
  );
}

/* ── Inspiración ───────────────────────────────────────── */

/**
 * Animated mockup for the daily-template Inspiración feature: a small
 * "template card" cycles through three niches (restaurante / fitness /
 * ecommerce) with a fresh image gradient + caption each beat. Two faint
 * cards peek from behind to suggest a deck of generated templates.
 */
const INSP_NICHES = [
  {
    name: 'Restaurante',
    emoji: '🍝',
    caption: 'Las 3 razones por las que tu menú no convierte',
    gradient: 'from-amber-400 via-orange-300 to-rose-300',
    pillBg: 'bg-amber-100',
    pillText: 'text-amber-700',
    animate: 'animate-feat-insp-1',
  },
  {
    name: 'Fitness',
    emoji: '💪',
    caption: 'El truco para no abandonar el gym en enero',
    gradient: 'from-emerald-400 via-teal-300 to-sky-300',
    pillBg: 'bg-emerald-100',
    pillText: 'text-emerald-700',
    animate: 'animate-feat-insp-2',
  },
  {
    name: 'Ecommerce',
    emoji: '🛒',
    caption: 'Cómo subir tu conversión sin bajar precios',
    gradient: 'from-violet-400 via-fuchsia-300 to-pink-300',
    pillBg: 'bg-violet-100',
    pillText: 'text-violet-700',
    animate: 'animate-feat-insp-3',
  },
] as const;

export function InspirationFeatureMockup() {
  return (
    <div className="relative h-full flex items-center justify-center">
      {/* Background stacked cards for "deck" feel */}
      <div
        aria-hidden
        className="absolute top-3 bottom-3 left-7 right-3 rounded-2xl bg-white border border-slate-200 shadow-sm rotate-[-5deg]"
      />
      <div
        aria-hidden
        className="absolute top-2 bottom-2 left-4 right-6 rounded-2xl bg-white border border-slate-200 shadow-sm rotate-[3deg]"
      />

      {/* Active template card */}
      <div className="relative w-full max-w-[230px] rounded-2xl bg-white border border-slate-200 shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-2.5 pt-2 pb-1.5">
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center h-5 w-5 rounded-md bg-gradient-to-br from-amber-400 to-pink-500 text-white shadow-sm">
              <Sparkles size={10} className="animate-feat-insp-spark" />
            </span>
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">
              Hoy
            </span>
          </div>
          <span className="inline-flex items-center gap-0.5 text-[9px] text-brand-600 font-semibold">
            <Plus size={9} /> Copiar
          </span>
        </div>

        {/* Image area: 3 niches stacked, fading */}
        <div className="relative mx-2.5 h-20 rounded-xl overflow-hidden">
          {INSP_NICHES.map((n) => (
            <div
              key={n.name}
              className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br ${n.gradient} ${n.animate}`}
            >
              <span className="text-[34px] leading-none drop-shadow-sm">{n.emoji}</span>
              <span
                className={`absolute top-1.5 left-1.5 inline-flex items-center px-1.5 py-0.5 rounded-md ${n.pillBg} ${n.pillText} text-[8px] font-bold uppercase tracking-wider shadow-sm`}
              >
                {n.name}
              </span>
            </div>
          ))}
        </div>

        {/* Caption area: 3 captions stacked, fading */}
        <div className="relative px-2.5 pt-2 pb-2.5 h-12">
          {INSP_NICHES.map((n) => (
            <p
              key={n.name}
              className={`absolute inset-x-2.5 top-2 text-[10px] leading-snug text-slate-700 font-medium ${n.animate}`}
            >
              {n.caption}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── AI Caption ────────────────────────────────────────── */

/**
 * Animated mockup for the "Genera copys en segundos" feature: a small post
 * preview at the top with a glowing "Generar con IA" button, and below a
 * caption card that streams in three different AI-written captions for the
 * same photo. The caret blinks continuously to suggest the AI is still typing.
 */
const AICAP_LINES = [
  {
    label: 'Versión cercana',
    text: 'El truco de la abuela para que esta paella te salga así de bonita 🥘✨',
    animate: 'animate-feat-aicap-1',
  },
  {
    label: 'Versión gancho',
    text: '¿Sabías que esta paella esconde un truco que llevamos haciendo desde 1972?',
    animate: 'animate-feat-aicap-2',
  },
  {
    label: 'Versión directa',
    text: 'Esta es, oficialmente, nuestra mejor paella del año. Y abrimos esta semana.',
    animate: 'animate-feat-aicap-3',
  },
] as const;

export function AiCaptionFeatureMockup() {
  return (
    <div className="relative h-full flex flex-col gap-2">
      {/* Photo preview card */}
      <div className="relative h-[72px] rounded-xl overflow-hidden border border-slate-200 shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-300 via-orange-300 to-rose-300 flex items-center justify-center">
          <span className="text-3xl drop-shadow-sm">🥘</span>
        </div>
        {/* Sparkle/AI badge on the photo */}
        <button
          type="button"
          className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gradient-to-br from-violet-600 to-pink-500 text-white text-[9px] font-bold shadow-md animate-feat-aicap-glow"
        >
          <Sparkles size={9} className="animate-feat-insp-spark" />
          Generar con IA
        </button>
      </div>

      {/* Caption card with cycling AI suggestions */}
      <div className="relative flex-1 rounded-xl bg-slate-50 border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-2 pt-1.5 pb-1 border-b border-slate-200/70">
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center justify-center h-3.5 w-3.5 rounded-sm bg-gradient-to-br from-violet-500 to-pink-500 shadow-sm">
              <Sparkles size={8} className="text-white" />
            </span>
            <span className="text-[8px] font-bold uppercase tracking-wider text-slate-500">
              Caption sugerido
            </span>
          </div>
          <div className="flex items-center gap-0.5">
            <span className="h-1 w-1 rounded-full bg-emerald-400 animate-feat-aicap-caret" />
            <span className="text-[8px] font-semibold text-emerald-600">live</span>
          </div>
        </div>

        {/* Caption body with stacked cycling lines */}
        <div className="relative px-2 pt-1.5 pb-2">
          {AICAP_LINES.map((l) => (
            <div key={l.label} className={`absolute inset-x-2 top-1.5 ${l.animate}`}>
              <span className="text-[7.5px] font-semibold text-slate-400 uppercase tracking-wider">
                {l.label}
              </span>
              <p className="mt-0.5 text-[10px] leading-snug text-slate-700 font-medium">
                {l.text}
                <span className="inline-block w-0.5 h-2.5 bg-brand-600 align-middle ml-0.5 animate-feat-aicap-caret" />
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── API ────────────────────────────────────────────────── */

export function ApiFeatureMockup() {
  return (
    <div className="relative h-full flex flex-col gap-2">
      {/* Terminal card */}
      <div className="rounded-xl bg-slate-900 text-slate-100 shadow-md overflow-hidden flex-1 min-h-0">
        <div className="flex items-center gap-1 px-2.5 py-1 bg-slate-800/80 border-b border-slate-700/60">
          <span className="h-2 w-2 rounded-full bg-rose-400" />
          <span className="h-2 w-2 rounded-full bg-amber-400" />
          <span className="h-2 w-2 rounded-full bg-emerald-400" />
          <span className="ml-1 text-[9px] text-slate-400 inline-flex items-center gap-1">
            <Terminal size={9} /> bash
          </span>
        </div>
        <div className="p-2 font-mono text-[9.5px] leading-snug">
          <div><span className="text-emerald-400">$</span> <span className="text-slate-300">curl -X POST</span></div>
          <div className="text-sky-300 truncate">  /api/v1/posts \</div>
          <div className="text-slate-300 truncate">  -H <span className="text-amber-300">"Auth: zpk_…"</span> \</div>
          <div className="mt-0.5 flex items-center">
            <span className="text-emerald-400">$</span>
            <span className="ml-1 inline-block h-2.5 w-1 bg-emerald-400 align-middle animate-feat-api-caret" />
          </div>
        </div>
      </div>

      {/* Response card - compact horizontal */}
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-2 py-1.5 shadow-sm flex items-center gap-2 animate-feat-api-pulse">
        <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-600 text-white text-[8px] font-bold uppercase tracking-wider flex-none">
          <Code size={8} /> 201
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold text-emerald-900 leading-tight truncate">
            Post programado
          </div>
          <div className="text-[8.5px] text-emerald-700/80 font-mono truncate">
            id: 7f3a-9e1c…
          </div>
        </div>
        <div className="flex items-center -space-x-1 flex-none">
          {[FaInstagram, FaXTwitter, FaLinkedin].map((Icon, i) => (
            <span
              key={i}
              className="h-4 w-4 rounded-full bg-white border border-emerald-200 flex items-center justify-center"
            >
              <Icon size={8} className="text-slate-700" />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── MCP / ChatGPT connector ────────────────────────────── */

/**
 * Animated mockup for the MCP feature: a mini chat where the user asks in
 * natural language and Zettapost (via the connector) confirms the action.
 */
export function McpFeatureMockup() {
  return (
    <div className="relative h-full flex flex-col gap-2">
      {/* Chat card */}
      <div className="rounded-xl bg-white border border-slate-200 shadow-md overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 border-b border-slate-200">
          <span className="h-4 w-4 rounded-full bg-slate-900 flex items-center justify-center">
            <Bot size={9} className="text-white" />
          </span>
          <span className="text-[10px] font-semibold text-slate-700">ChatGPT</span>
          <span className="ml-auto inline-flex items-center gap-1 text-[8px] font-medium text-emerald-600">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Zettapost
          </span>
        </div>
        <div className="p-2 flex flex-col gap-1.5">
          {/* user bubble */}
          <div className="self-end max-w-[88%] rounded-lg rounded-br-sm bg-brand-600 text-white px-2 py-1 text-[9.5px] leading-snug shadow-sm animate-feat-api-pulse">
            Publica esto en Instagram y X mañana a las 10:00
          </div>
          {/* assistant bubble */}
          <div className="self-start max-w-[92%] rounded-lg rounded-bl-sm bg-slate-100 text-slate-700 px-2 py-1 text-[9.5px] leading-snug">
            Hecho ✅ Programado en 2 redes para mañana a las 10:00.
          </div>
        </div>
      </div>

      {/* Confirmation chip */}
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-2 py-1.5 shadow-sm flex items-center gap-2">
        <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-600 text-white text-[8px] font-bold uppercase tracking-wider flex-none">
          <Check size={8} /> OK
        </div>
        <div className="flex-1 min-w-0 text-[10px] font-bold text-emerald-900 leading-tight truncate">
          Programado · sin salir del chat
        </div>
        <div className="flex items-center -space-x-1 flex-none">
          {[FaInstagram, FaXTwitter].map((Icon, i) => (
            <span
              key={i}
              className="h-4 w-4 rounded-full bg-white border border-emerald-200 flex items-center justify-center"
            >
              <Icon size={8} className="text-slate-700" />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Per-network caption ───────────────────────────────── */

/**
 * Animated mockup of the per-network caption editor at /dashboard/posts/new:
 * tabs that cycle through three networks, with the textarea swapping to the
 * caption style appropriate to each (concise+emojis for Instagram, a thread
 * teaser for X, professional for LinkedIn).
 */
export function PerNetworkCaptionFeatureMockup() {
  const tabs: { Icon: typeof FaInstagram; name: string; color: string }[] = [
    { Icon: FaInstagram, name: 'Instagram', color: '#E4405F' },
    { Icon: FaXTwitter,  name: 'X',         color: '#0f172a' },
    { Icon: FaLinkedin,  name: 'LinkedIn',  color: '#0A66C2' },
  ];
  const captions = [
    '✨ Acabamos de lanzar la v2 — todo más rápido, más limpio, más tuyo 💙\n\n#producto #lanzamiento',
    'La v2 ya está aquí. Más rápido. Más limpio. Más tuyo. Hilo abajo 🧵',
    'Tras 6 meses de trabajo, lanzamos la versión 2 de nuestro producto. Estas son las 3 mejoras que más impacto tendrán en tu día a día →',
  ];
  const tabAnims = ['animate-feat-cap-tab-1', 'animate-feat-cap-tab-2', 'animate-feat-cap-tab-3'] as const;

  return (
    <div className="relative h-full flex items-center">
      <div className="w-full rounded-xl border border-slate-200 bg-white shadow-md p-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold text-slate-900">Texto de la publicación</span>
          <span className="text-[9px] text-slate-400">3 redes</span>
        </div>

        {/* Tabs: "General" + 1 per network */}
        <div className="flex flex-wrap gap-1 mb-2 pb-2 border-b border-slate-100">
          <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-md border border-slate-200 text-slate-500 bg-white">
            <Sparkles size={8} /> General
          </span>
          {tabs.map((t, i) => (
            <span
              key={t.name}
              className={`relative inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-md border ${tabAnims[i]}`}
              style={{
                borderColor: t.color,
                background: `${t.color}10`,
                color: t.color,
              }}
            >
              <t.Icon size={8} />
              {t.name}
              <span
                className="ml-0.5 h-1 w-1 rounded-full bg-amber-500"
                aria-hidden
              />
            </span>
          ))}
        </div>

        {/* Banner */}
        <div className="rounded-md bg-amber-50/70 border border-amber-200 px-2 py-1 mb-1.5 flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-[8.5px] font-semibold text-amber-700">
            <span className="h-1 w-1 rounded-full bg-amber-500" />
            Personalizado
            <Sparkles size={8} className="text-amber-600 animate-feat-cap-sparkle" />
          </span>
          <span className="text-[8px] text-slate-400">Usar general</span>
        </div>

        {/* Textareas stacked, cycled with the tabs */}
        <div className="relative h-[58px] rounded-md border border-amber-200 bg-white px-2 py-1.5 text-[9px] leading-snug text-slate-700 overflow-hidden">
          {captions.map((cap, i) => (
            <span
              key={i}
              className={`absolute inset-0 px-2 py-1.5 whitespace-pre-line ${tabAnims[i]}`}
            >
              {cap}
            </span>
          ))}
        </div>

        {/* Footer hint */}
        <p className="mt-1.5 text-[8.5px] text-slate-400 leading-tight">
          Este texto solo se usará en esta red. El general no se modifica.
        </p>
      </div>
    </div>
  );
}

/* ── Client approval ────────────────────────────────────── */
export function ApprovalFeatureMockup() {
  const rows: { Icon: typeof FaInstagram; name: string; color: string; state: 'approved' | 'changes' }[] = [
    { Icon: FaInstagram, name: 'Instagram', color: '#E4405F', state: 'approved' },
    { Icon: FaLinkedin, name: 'LinkedIn', color: '#0A66C2', state: 'changes' },
  ];
  return (
    <div className="relative h-full flex items-center">
      <div className="w-full rounded-xl border border-slate-200 bg-white shadow-md p-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-900">
            <UserCheck size={11} className="text-brand-600" /> Revisión del cliente
          </span>
          <span className="inline-flex items-center gap-1 text-[8.5px] font-medium px-1.5 py-0.5 rounded-full text-amber-700 bg-amber-50 border border-amber-200">
            Pendiente
          </span>
        </div>

        {/* Mini post */}
        <div className="flex items-center gap-2 rounded-md border border-slate-100 bg-slate-50/60 p-1.5 mb-2">
          <div className="h-7 w-7 rounded bg-gradient-to-br from-brand-400 to-brand-600 shrink-0" />
          <div className="min-w-0">
            <div className="h-1.5 w-28 rounded bg-slate-300 mb-1" />
            <div className="h-1.5 w-20 rounded bg-slate-200" />
          </div>
        </div>

        {/* Per-network decisions */}
        <div className="space-y-1">
          {rows.map((r) => (
            <div key={r.name} className="flex items-center justify-between rounded-md border border-slate-100 px-2 py-1">
              <span className="inline-flex items-center gap-1.5 text-[9px] font-medium text-slate-700">
                <r.Icon size={10} color={r.color} /> {r.name}
              </span>
              {r.state === 'approved' ? (
                <span className="inline-flex items-center gap-1 text-[8.5px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                  <Check size={9} /> Aprobado
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[8.5px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5">
                  <Pencil size={9} /> Cambios
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <p className="mt-2 inline-flex items-center gap-1 text-[8.5px] text-slate-400 leading-tight">
          <Mail size={9} /> Enviado a los revisores por email
        </p>
      </div>
    </div>
  );
}

/* ── Teams ──────────────────────────────────────────────── */
export function TeamFeatureMockup() {
  const members: {
    initial: string; name: string; color: string; role: string;
    roleCls: string; nets: { Icon: typeof FaInstagram; color: string }[];
  }[] = [
    { initial: 'P', name: 'Pablo', color: 'from-brand-400 to-brand-600', role: 'Dueño', roleCls: 'text-brand-700 bg-brand-50 border-brand-200', nets: [{ Icon: FaInstagram, color: '#E4405F' }, { Icon: FaXTwitter, color: '#0F1419' }, { Icon: FaLinkedin, color: '#0A66C2' }] },
    { initial: 'L', name: 'Lucía', color: 'from-fuchsia-400 to-fuchsia-600', role: 'Editora', roleCls: 'text-slate-600 bg-slate-100 border-slate-200', nets: [{ Icon: FaInstagram, color: '#E4405F' }, { Icon: FaTiktok, color: '#010101' }] },
    { initial: 'M', name: 'Marc', color: 'from-emerald-400 to-emerald-600', role: 'Editor', roleCls: 'text-slate-600 bg-slate-100 border-slate-200', nets: [{ Icon: FaLinkedin, color: '#0A66C2' }] },
  ];
  return (
    <div className="relative h-full flex items-center">
      <div className="w-full rounded-xl border border-slate-200 bg-white shadow-md p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-900">
            <Users size={11} className="text-brand-600" /> Tu equipo
          </span>
          <span className="text-[8.5px] font-medium px-1.5 py-0.5 rounded-full text-slate-500 bg-slate-100 border border-slate-200">
            3 miembros
          </span>
        </div>
        <div className="space-y-1">
          {members.map((m) => (
            <div key={m.name} className="flex items-center gap-2 rounded-md border border-slate-100 px-2 py-1.5">
              <span className={`h-6 w-6 rounded-full bg-gradient-to-br text-white text-[10px] font-bold flex items-center justify-center shrink-0 ${m.color}`}>
                {m.initial}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[10px] font-semibold text-slate-800 leading-tight">{m.name}</span>
                <span className={`inline-flex items-center text-[8px] font-medium px-1 py-px rounded-full border mt-0.5 ${m.roleCls}`}>{m.role}</span>
              </span>
              <span className="flex items-center -space-x-1 shrink-0">
                {m.nets.map((n, i) => (
                  <span key={i} className="h-4 w-4 rounded-full bg-white border border-slate-200 flex items-center justify-center">
                    <n.Icon size={8} color={n.color} />
                  </span>
                ))}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-2 inline-flex items-center gap-1 text-[8.5px] text-slate-400 leading-tight">
          <Plus size={9} /> Invita por email o enlace · permisos por sección y por cuenta
        </p>
      </div>
    </div>
  );
}

/* ── 8) Timezone ────────────────────────────────────────── */

export function TimezoneFeatureMockup() {
  return (
    <div className="relative h-full flex items-center gap-3">
      {/* Clock face */}
      <div className="relative flex-none">
        <div className="h-20 w-20 rounded-full bg-gradient-to-br from-brand-50 to-white border border-slate-200 shadow-inner relative">
          {/* hour markers */}
          {[0, 90, 180, 270].map((deg) => (
            <div
              key={deg}
              className="absolute left-1/2 top-1 w-px h-2 bg-slate-300"
              style={{ transform: `translateX(-50%) rotate(${deg}deg)`, transformOrigin: '50% calc(50% + 38px)' }}
            />
          ))}
          {/* hands */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full">
            <div className="w-0.5 h-6 bg-slate-700 rounded-full origin-bottom animate-feat-hour" />
          </div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full">
            <div className="w-0.5 h-7 bg-brand-600 rounded-full origin-bottom animate-feat-min" />
          </div>
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-brand-600" />
        </div>
      </div>

      {/* Zone rows */}
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <Globe size={10} className="text-brand-600 flex-none" />
            <span className="text-[10px] font-semibold text-slate-700 truncate">Madrid</span>
          </div>
          <span className="text-[10px] font-bold tabular-nums text-slate-900">14:00</span>
        </div>
        <div className="flex items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <Globe size={10} className="text-slate-400 flex-none" />
            <span className="text-[10px] font-semibold text-slate-700 truncate">México</span>
          </div>
          <span className="text-[10px] font-bold tabular-nums text-slate-900">06:00</span>
        </div>
        <div className="flex items-center justify-between gap-2 rounded-lg bg-white border border-slate-200 px-2.5 py-1.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <Globe size={10} className="text-slate-400 flex-none" />
            <span className="text-[10px] font-semibold text-slate-700 truncate">Tokio</span>
          </div>
          <span className="text-[10px] font-bold tabular-nums text-slate-900">21:00</span>
        </div>
      </div>
    </div>
  );
}

/* ── 9) Analytics ───────────────────────────────────────── */

export function AnalyticsFeatureMockup() {
  const bars = [38, 62, 48, 78, 55, 90, 70];
  const days = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
  return (
    <div className="relative flex h-full items-center gap-4">
      {/* Bars */}
      <div className="flex-1 h-full flex flex-col">
        <div className="flex items-end justify-between gap-1.5 flex-1 mb-1">
          {bars.map((v, i) => (
            <div key={i} className="flex-1 flex items-end" style={{ height: '100%' }}>
              <div
                className="w-full rounded-t bg-gradient-to-t from-brand-500 to-brand-300 animate-feat-bar"
                style={{ height: `${v}%`, animationDelay: `${i * 0.1}s` }}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-1.5">
          {days.map((d) => (
            <div key={d} className="flex-1 text-center text-[8px] text-slate-400 font-semibold">{d}</div>
          ))}
        </div>
      </div>

      {/* Metric cards */}
      <div className="flex-none space-y-1.5 w-32">
        <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
          <div className="text-[9px] text-slate-500 font-medium flex items-center gap-1">
            <Heart size={9} /> Likes
          </div>
          <div className="text-base font-extrabold text-slate-900 tabular-nums">12.4K</div>
          <div className="text-[9px] font-bold text-emerald-600 flex items-center gap-0.5">
            <TrendingUp size={9} /> +18%
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
          <div className="text-[9px] text-slate-500 font-medium flex items-center gap-1">
            <Eye size={9} /> Alcance
          </div>
          <div className="text-base font-extrabold text-slate-900 tabular-nums">94K</div>
          <div className="text-[9px] font-bold text-emerald-600 flex items-center gap-0.5">
            <TrendingUp size={9} /> +6%
          </div>
        </div>
      </div>
    </div>
  );
}
